// Sugalarm — the Now screen. Driven entirely by the live WebSocket state
// (`socket.lastReading` / `recentReadings`) plus a periodic pull of
// /api/treatments for the IOB rings.
//
// Two layout flavors:
//   kiosk → fixed 1024×600 design (224px hero, 1fr / 360px grid)
//   web   → fluid (clamp() hero, side rail collapses below 960px)

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import * as alertsApi from "../../api/alerts";
import * as glucoseApi from "../../api/glucose";
import * as treatmentsApi from "../../api/treatments";
import { BolusRings } from "../../components/glucose/BolusRings";
import { HeroValue } from "../../components/glucose/HeroValue";
import { HistoryChart } from "../../components/glucose/HistoryChart";
import { IobPager } from "../../components/glucose/IobPager";
import { ReadingDetailPopover } from "../../components/glucose/ReadingDetailPopover";
import { SensorPausePrompt } from "../../components/kiosk/SensorPausePrompt";
import { Sparkline } from "../../components/glucose/Sparkline";
import { TrendArrow } from "../../components/glucose/TrendArrow";
import { TrendSheet } from "../../components/glucose/TrendSheet";
import { HighAlarmBanner } from "../../components/kiosk/HighAlarmBanner";
import { LowAlarmBanner } from "../../components/kiosk/LowAlarmBanner";
import { AlarmSilenceGuard } from "../../components/kiosk/AlarmSilenceGuard";
import { AudioLeadScreen } from "../../components/kiosk/AudioLeadScreen";
import AckFailureNotice from "../../components/kiosk/AckFailureNotice";
import { CustodyPrompt } from "../../components/kiosk/CustodyPrompt";
import { TreatingScreen } from "../../components/kiosk/TreatingScreen";
import { TreatingSheet } from "../../components/kiosk/TreatingSheet";
import { WhoTreatingSheet } from "../../components/kiosk/WhoTreatingSheet";
import {
  isSelfTreating,
  pickerLatchStep,
  pickerMayOpen,
  pickerMustClose,
  useAlarmSoundingForPicker,
} from "../../lib/attributionPicker";
import { iobChipEntries } from "../../lib/iobChip";
import { fmtTreatingRemaining } from "../../lib/treating";
import { ringFraction, ringTotalMs } from "../../lib/treatingCheckin";
import { UrgentBanner } from "../../components/kiosk/UrgentBanner";
import AlarmReliabilityBanner from "../../components/mobile/AlarmReliabilityBanner";
import { ActiveBolusesModal } from "../../components/ui/ActiveBolusesModal";
import { ConfirmDeleteModal } from "../../components/ui/ConfirmDeleteModal";
import { Pill } from "../../components/ui/Pill";
import { statusPillClass } from "../../components/ui/statusPill";
import {
  TREATING_IDENTITY_AUTO_DISMISS_MS,
  treatingErrorMessage,
  treatingSurfaceMounted,
} from "../../lib/treating";
import {
  HANDBACK_LINE_MS,
  custodyHandBackLine,
} from "../../lib/treatingCheckin";
import {
  OPT_OUT_UNDO_LABEL,
  optOutUndoVisible,
} from "../../lib/heroOptOut";
import type { GlucoseSocketState } from "../../hooks/useGlucoseSocket";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import { useIsLandscape } from "../../hooks/useIsLandscape";
import { useMyLinkedContactId } from "../../hooks/useMyLinkedContactId";
import { useSettings } from "../../hooks/useSettings";
import { patch as patchUserSettings } from "../../api/settings";
import { useThresholds } from "../../hooks/useThresholds";
import { hapticSelect } from "../../lib/haptics";
import { ascentHoldLine } from "../../lib/ascentLatch";
import { recoveringHeaderLine } from "../../lib/alarmStandDown";
import {
  displayValue,
  fmtAge,
  recentDeltaMgdl,
  toMmol,
  trendInfo,
  zoneColor,
  zoneLabel,
  zoneOf,
  zoneTone,
} from "../../lib/glucose";
import { followerStoppedLine } from "../../lib/serviceLock";
import { glucoseWindowStats } from "../../lib/glucoseStats";
import {
  activeBolusesForRings,
  basalForContext,
  BASAL_LOOKBACK_HOURS,
  CORRECTION_BOLUS_COLOR,
  DEFAULT_BASAL_DURATION_MIN,
  IOB_DURATION_MS,
  isCorrectionBolus,
  MEAL_BOLUS_COLOR,
  mealForContext,
  type BasalContext,
  type BolusForRings,
  type MealContextBlock,
} from "../../lib/iob";
import { MealContextLines } from "../../components/glucose/MealContextLines";
import {
  type Alert,
  type Member,
  type Reading,
  type Treatment,
  type Zone,
} from "../../lib/types";
// Cycle 33: QuickLogRow is no longer rendered on the Now screen.
// The component file is retained for a future cycle that may revive
// it cleanly. Logging surfaces today: FooterTabs → Log on kiosk/web,
// and the same Log tab on mobile (cycle 30 redesign owns the flow).
import styles from "./NowScreen.module.css";

import type { AlarmEpisodeState } from "../../hooks/useAlarmEpisode";
import type { ClockMode } from "../../hooks/useAlarmClockMode";
import { AlarmClockMode } from "../clock/AlarmClockMode";
import { selectCurrentGlucoseFor } from "../../lib/currentGlucose";

interface Props {
  socket: GlucoseSocketState;
  now: number;
  variant: FrameVariant;
  /** Cycle 222 — the cycle-214 status orb + under-hero caption were moved
   *  OFF the Now hero into the frame-level `SpeakingBanner`, so NowScreen no
   *  longer consumes the TTS surface (the `tts` prop was threaded out). */
  /** Currently-active member, resolved by `useActiveMember`. */
  activeMember: Member | null;
  /** Display units, from the app-wide settings context. */
  units: "mg/dL" | "mmol/L";
  /** Cycle 20.x — when phase !== idle the screen renders as an alarm
   *  display (red flashing background + Address / Silence / countdown)
   *  instead of the normal hero + side-column layout. */
  episode: AlarmEpisodeState;
  /** Cycle 101 — count of voice-logged treatments waiting to sync (offline
   *  queue). Rendered as a small unobtrusive hint near the IOB card on the
   *  kiosk. 0 → nothing shown. */
  pendingSyncCount?: number;
  /** Cycle 402 — the frame-level no-data alarm banner is mounted. On the
   *  bedside face that banner is FIXED to the bottom edge, so the face has
   *  to reserve the band or the strip lands on the clock digits (cycle
   *  394's rule).
   *
   *  ⚠ THIS IS THE SAME `noDataAlarmActive` THE FRAME MOUNTS ON, threaded
   *  from App.tsx — deliberately NOT a re-derived predicate. A second copy
   *  of the mount condition is how the face ends up reserving a band for a
   *  strip that is not there (dead space under the heroes) or, worse,
   *  failing to reserve for one that is. */
  noDataAlarmActive?: boolean;
  /** Cycle 468: the active member's owner is locked out (the server's
   *  `service_state`). A hero viewing that member sees the stopped line in
   *  place of readings and No Data. The owner's own lockout never reaches
   *  this screen (App.tsx shows the lockout instead of the route). */
  serviceLocked?: boolean;
  /** Cycle 451.1: SUSTAINED alarm audio is sounding (the same value the
   *  return-to-Now rule pins on).
   *
   *  ⚠ EXPLANATION ONLY. No alarm control, no sound, no gate and no
   *  custody decision reads this. It exists so the Log affordances can say
   *  why they are not navigating: while the rule is armed a tap on Log
   *  would be bounced straight back to `/now`, and a control that silently
   *  does nothing on an alarm surface is not something this app ships. */
  alarmSounding?: boolean;
  /** Cycle 452: open Log as an overlay on `/now` (no route change) — the
   *  shell owns it. While sustained audio sounds the Log affordances call
   *  this instead of navigating. Absent → the cycle-451.1 notice, which is
   *  kept as the fallback. */
  onOpenLogOverlay?: (to: string) => void;
  /** Cycle 407 — the app-level TTS `speak` (ONE owner: the same instance
   *  reminders and auto-announce use, already primed by the tap that opened
   *  the window), threaded so the treating screen's check-in is spoken
   *  through it. It deliberately bypasses `tts_quiet_hours`: this is
   *  alarm-path speech inside a live urgent-low episode. Optional — with
   *  no speech, the chime + the visual carry the check-in. */
  speak?: (text: string) => void;
  /** Cycle 404 — the signed-in account id (`auth.user.id`), threaded from
   *  the shell so the "I can help after all" action can tell whether the
   *  person holding THIS screen is one of the household's linked heroes.
   *
   *  ⚠ IDENTITY IS NOT A GATE ON ANY ALARM CONTROL. Nothing on this screen
   *  appears, disappears, sounds or stops because of this value; it decides
   *  one SECONDARY affordance and nothing else. Null (signed out, or an
   *  older shell that does not pass it) simply means no affordance.
   *
   *  It is deliberately NOT enough on its own: a kiosk device session
   *  authenticates AS the household owner, so a matching id resolves there
   *  too — the `variant !== "kiosk"` term is what protects the tablet. */
  userId?: string | null;
  /** Cycle 145 — alarm-clock-mode controller from the shell. When active,
   *  the bedside face renders in place of the normal Now content, BELOW
   *  the alarm banner row (and the urgent takeover early-returns before
   *  it, so the takeover always wins). */
  clockMode?: ClockMode;
}

function relationshipLabel(m: Member | null): string {
  if (!m) return "MEMBER";
  if (m.is_self) return "YOU";
  if (m.relationship_label) return m.relationship_label.toUpperCase();
  return "MEMBER";
}

function fmtSnoozeNow(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${String(ss).padStart(2, "0")}`;
}

// Cycle 51: the Now-screen chart window, in hours. 168 = 7 days,
// 720 = 30 days — the chart fetch + range math are all hours-based, so
// day windows are just their hour count; only the pill label maps back
// to "7d" / "30d". Stays local to this screen (no other consumer).
// Cycle 65: the 3-hour mobile-only pill is retired — every variant
// (kiosk, web, mobile) now uses the same six-window set: 2h · 6h ·
// 12h · 24h · 7d · 30d. Mobile lets the pill row wrap to fit on a
// 390-px phone without overflow.
type ChartHours = 2 | 6 | 12 | 24 | 168 | 720;

// Cycle 163.1: localStorage key for the dismissed basal-due nudge
// (stores the dismissed dose's `takenAt` ms).
const BASAL_DUE_DISMISS_KEY = "sugalarm.basalDueDismissedAt";

// Windows that pan a fixed slice (cycle 48 + 51 + 65). The slice
// WIDTH is per-variant for the short windows and a shared 3-day
// default for the long ones (see `panSliceHours` below).
// 0.65.2: the 2h pill now also pans — a 1h slice on every variant —
// so the dots render full-size and the user can swipe the hour. It's
// always pannable (no expand-chevron gate) on every variant, since a
// 2h-wide static plot crammed the dots together.
const PANNABLE_HOURS: readonly ChartHours[] = [2, 6, 12, 24, 168, 720];

// Cycle 65: long-window (7d / 30d) initial visible slice — 3 days
// anchored at "now". Pinch/pan walks the rest of the loaded data.
// Single named constant so a future tweak is one edit.
const LONG_WINDOW_DEFAULT_VISIBLE_HOURS = 72;
const isLongWindow = (h: ChartHours): boolean => h === 168 || h === 720;

function windowLabel(h: ChartHours): string {
  if (h === 168) return "7d";
  if (h === 720) return "30d";
  return `${h}h`;
}

export function NowScreen({
  socket,
  now,
  variant,
  activeMember,
  units,
  episode,
  pendingSyncCount = 0,
  clockMode,
  noDataAlarmActive = false,
  serviceLocked = false,
  alarmSounding = false,
  onOpenLogOverlay,
  userId = null,
  speak,
}: Props) {
  const thresholds = useThresholds();
  const { settings, applyRemoteUpdate } = useSettings();
  const h24 = settings?.display_24h ?? false;
  const nav = useNavigate();
  // Cycle 114: layout-within-mobile orientation (single-owner hook). Only
  // the mobile variant uses it; kiosk/web ignore it entirely.
  const isLandscape = useIsLandscape();
  const mobileLandscape = variant === "mobile" && isLandscape;
  // ⚠ CYCLE 435 — THE NUMBER COMES FROM THE ONE SELECTOR, WHICH SAMPLES
  // ITS OWN CLOCK. On 2026-09-14 20:27Z this screen painted 76 — a reading
  // from 13:47 — on the first frame after a thaw, because it judged the
  // reading against the ticking `now` prop, and that clock had frozen with
  // the number. `selectCurrentGlucoseFor` takes `Date.now()` at this call;
  // `now` below is used for LABELS and the treatment maths only, never for
  // a freshness decision. `current` is the ONLY value any hero may paint.
  const view = selectCurrentGlucoseFor(socket, activeMember?.id ?? socket.activeMemberId);
  const current = view.displayValue;
  const latestId = view.reading?.id ?? null;
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  // Cycle 43: the tappable IOB ring now opens <ActiveBolusesModal> —
  // an inline editor over *every* active bolus, not cycle-42's
  // read-only single-bolus view. `iobModalOpen` drives that modal;
  // `pendingDelete` drives the sibling-mounted <ConfirmDeleteModal>
  // when a row's `×` is tapped (the active modal stays open behind it).
  const [iobModalOpen, setIobModalOpen] = useState<boolean>(false);
  const [pendingDelete, setPendingDelete] = useState<Treatment | null>(null);
  // Cycle 24: live treatments arrive via the WS `treatment` /
  // `treatment_deleted` frames. The hook's per-member cache feeds in
  // here so the IOB ring + quick-log row update within a second of a
  // partner's phone-side log.
  const activeId = activeMember?.id ?? null;
  const liveTreatments = activeId
    ? socket.byMember[activeId]?.recentTreatments
    : undefined;
  // Cycle 20: side-column chart needs more readings than the WS
  // buffer (capped at ~3h) and its own alerts list. Window is
  // user-selectable (cycle 20.x).
  const [chartReadings, setChartReadings] = useState<Reading[]>([]);
  const [chartAlerts, setChartAlerts] = useState<Alert[]>([]);
  const [chartHours, setChartHours] = useState<ChartHours>(6);
  // Cycle 222: the cycle-214 status orb + its under-hero caption were moved
  // off NowScreen into the frame-level SpeakingBanner — the caption-mount
  // state/effect that lived here is gone with them.
  // Cycle 48: collapsed (cycle-47 layout) vs. expanded (chart fills
  // the whole content area, hero + IOB rings hidden, longer pannable
  // windows). Session-local — a fresh Now mount starts collapsed.
  // Kiosk never renders the toggle, so this stays false there and the
  // kiosk layout is byte-identical to cycle 47.
  const [chartExpanded, setChartExpanded] = useState(false);
  // Cycle 48: tapped reading dot → read-only detail popover.
  const [readingDetail, setReadingDetail] = useState<{
    reading: Reading;
    anchorRect: DOMRect;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Cycle 51: limits sized for the whole pannable window, not just
      // the visible slice — panning a 7d/30d window needs every row in
      // memory or older data would gap out. 30d Dexcom ≈ 8,640
      // readings (well under the 30k history cap); treatments/alerts
      // are far sparser but bumped clear of any 30d total (both server
      // caps are 10k). Small windows just return what exists.
      const [t, g, a] = await Promise.all([
        // Cycle 135: floor the treatments window at the basal lookback span
        // so `basalForContext` always has ≥48h of rows regardless of the
        // selected chart window — otherwise a basal logged earlier than a
        // 2h/6h/12h window never reaches the IOB card and the "L" line
        // vanishes. `max` keeps the full span at 24h/7d/30d. The chart
        // still clips treatment glyphs to the visible window (HistoryChart
        // renders null outside [viewStart, viewEnd]), so no off-window
        // glyphs leak onto a short window. Readings + alerts stay scoped
        // to `chartHours`.
        treatmentsApi.list(Math.max(chartHours, BASAL_LOOKBACK_HOURS), 10_000),
        glucoseApi.history(chartHours, 30_000),
        alertsApi.list(chartHours, 5_000),
      ]);
      if (cancelled) return;
      if (t.ok) setTreatments(t.data.treatments);
      if (g.ok) {
        const asc = [...g.data.readings].sort(
          (x, y) =>
            new Date(x.timestamp).getTime() -
            new Date(y.timestamp).getTime(),
        );
        setChartReadings(asc);
      }
      if (a.ok) setChartAlerts(a.data.alerts);
    })();
    return () => {
      cancelled = true;
    };
  }, [latestId, chartHours, activeId]);

  const deletedTreatmentIds = activeId
    ? socket.byMember[activeId]?.deletedTreatmentIds
    : undefined;

  // Merge any WS-arriving treatments into the REST-seeded list,
  // dedup'd by id and ordered newest-first to match the REST shape.
  // Deletions arrive as ids in `deletedTreatmentIds`; filter them out
  // so the IOB ring drops the bolus even if the REST seed still holds
  // it.
  useEffect(() => {
    setTreatments((prev) => {
      const byId = new Map<string, Treatment>();
      for (const t of prev) byId.set(t.id, t);
      if (liveTreatments) {
        for (const t of liveTreatments) byId.set(t.id, t);
      }
      if (deletedTreatmentIds) {
        for (const id of deletedTreatmentIds) byId.delete(id);
      }
      const next = Array.from(byId.values()).sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      );
      // Avoid resetting state when nothing actually changed (prevents
      // a render storm on every WS pong).
      if (
        next.length === prev.length &&
        next.every((t, i) => t.id === prev[i]!.id)
      ) {
        return prev;
      }
      return next;
    });
  }, [liveTreatments, deletedTreatmentIds]);

  // ⚠ CYCLE 433 — AN UNKNOWN READING LOOKS UNKNOWN, AND THIS IS A DISPLAY
  // RULE. `readingUnknown` is true when the client cannot PROVE its copy is
  // current: nothing has confirmed the reading stream for longer than the
  // confirmation bound plus the reseed timeout, so the attempt to confirm
  // has demonstrably failed. It is folded into the SAME `stale` the hero
  // and the takeover already use, so the presentation is the cycle-427 No
  // Data one and not a second visual language.
  //
  // ⚠ It gates NOTHING but the number on an ALARM surface: the takeover
  // mounts on `episode.*`, its controls on `episode.phase`, and the sound
  // is derived in `useAlarmSounds` from `socket.lastReading` and the
  // server's rows — all untouched by this line, and all pinned by
  // `lib/__tests__/readingUnknown.test.ts`.
  //
  // ⚠ On the ORDINARY face it does more than the number, and deliberately
  // so: `noData` below inherits the whole cycle-375.1 dark-feed layout —
  // the single full-width column, the withheld side column (chart + IOB
  // card) and the sensor-pause strip. That IS the existing No Data
  // presentation, reused rather than forked, and withholding a trace that
  // ends minutes ago is the same principle as withholding the number.
  //
  // ⚠ Cycle 435: `stale` is exactly "the selector released no current
  // value". The server's own verdict (Incident 2), the sample's age in
  // seconds, and the per-member stream confirmation judged against a clock
  // sampled at this render (Incident 1) are all folded upstream in
  // `lib/currentGlucose.ts`; nothing here computes freshness any more.
  const stale = current == null;
  const zone: Zone = current ? zoneOf(current.sgv, thresholds) : "unknown";
  const ageMin = view.ageMinutes ?? 0;
  const trend = trendInfo(current?.trend ?? null, current?.trendRate ?? null);

  // Cycle 20 fix: show the actual mg/dL delta from the previous
  // reading (matches Dexcom / Nightscout convention) rather than the
  // hard-coded per-minute rate baked into the trend table — that
  // value is a category placeholder, not real data, and was producing
  // misleading numbers like "-1.5 mg/dL/min" when the true 5-min
  // delta was -15 mg/dL.
  const recent = socket.recentReadings;
  // Cycle 435: history maths lives in the lib; painted only beside a
  // CURRENT number (every hero below is gated on `noData`).
  const deltaMgdl: number | null = recentDeltaMgdl(recent);

  const tone = zoneTone(zone);
  const isUrgent = zone === "urgent_low" || zone === "urgent_high";

  // Cycle 222: the cycle-214 status orb + its under-hero caption were
  // RELOCATED off the Now hero into the frame-level <SpeakingBanner> (a
  // transient bottom strip shown only while speaking). NowScreen no longer
  // mounts the orb, derives its state, or binds the TTS surface — the banner
  // (mounted in KioskFrame / WebFrame / MobileFrame) owns the orb + caption +
  // Stop control now. The hero column returns to a clean layout with nothing
  // under the glucose number.

  // Cycle 75: the meal/dose lines are regrouped around the meal — meal
  // boluses that pair to one carbs row collapse into a single block so
  // the food + carb estimate render once. The rings stay per-bolus and
  // color-coded; `isCorrectionBolus` is the single source of truth for
  // ring color, computed directly per ring (meal → accent, correction →
  // amber).
  const blocks: MealContextBlock[] = mealForContext(treatments, now);
  const baseBoluses: BolusForRings[] = activeBolusesForRings(treatments, now);
  const boluses: BolusForRings[] = baseBoluses.map((b) => {
    // Cycle 118: match by treatment id, not timestamp. Two doses logged
    // at the same instant (e.g. a meal bolus + correction stacked at one
    // time) collided under the old `takenAt` match — `find` returned the
    // same row for both rings, so both took that row's color (both meal
    // or both correction). The dose-line dots were unaffected (they group
    // via mealForContext), so the legend read blue+yellow while the rings
    // were both one color. The id is unique per row, so each ring now
    // classifies from its own treatment.
    const row = treatments.find((t) => t.id === b.id);
    const correction = row ? isCorrectionBolus(row, treatments, now) : false;
    return {
      ...b,
      color: correction ? CORRECTION_BOLUS_COLOR : MEAL_BOLUS_COLOR,
    };
  });
  const hasIOB = boluses.length > 0;

  // Cycle 121: the long-acting (basal) fuel line. Excluded from the rings
  // above; surfaced as its own depleting "L" line on the same card.
  const basal: BasalContext | null = useMemo(
    () => basalForContext(treatments, now),
    [treatments, now],
  );
  // Cycle 163.1: the basal DUE nudge is dismissible via the glass × on
  // the IOB card. Device-local, keyed to the dose's takenAt so a newly
  // logged basal always brings the ring back fresh; only the lapsed
  // (due) state can be dismissed — an actively depleting basal cannot.
  const [basalDueDismissedAt, setBasalDueDismissedAt] = useState<number | null>(
    () => {
      const raw = localStorage.getItem(BASAL_DUE_DISMISS_KEY);
      const n = raw ? Number(raw) : NaN;
      return Number.isFinite(n) ? n : null;
    },
  );
  const dismissBasalDue = () => {
    if (!basal?.due) return;
    localStorage.setItem(BASAL_DUE_DISMISS_KEY, String(basal.takenAt));
    setBasalDueDismissedAt(basal.takenAt);
  };
  const basalForCard: BasalContext | null =
    basal && basal.due && basal.takenAt === basalDueDismissedAt ? null : basal;
  // Hide-the-IOB-card-when-empty: the card renders only when there's
  // something to show — active bolus IOB, meal context, OR a basal
  // (active or due, so a due Lantus keeps its nudge on screen).
  // Cycle 147: the basal renders as the third OUTERMOST ring on
  // BolusRings (the cycle-121 BasalLine meter is retired) and as a
  // violet Long-acting dose line in MealContextLines; the due nudge
  // ("Log {product}") lives on that line now.
  const hasMealContext = blocks.length > 0;
  const showRingsCard = hasIOB || hasMealContext;

  // Cycle 303: server-coordinated IOB-card dismissal. Pressing the card's
  // × PATCHes `iob_card_dismissed_at = now`; the settings_update fan-out
  // hides the card on every device (grant-holders). It hides only while
  // every context item currently driving the card is OLDER than the
  // dismissal — so any newer bolus/carbs/basal log automatically re-shows
  // it everywhere with no un-dismiss. A DUE basal always keeps it visible
  // (the "take your Lantus" nudge must not be dismissed into silence,
  // cycle-121 reasoning). State lives entirely in settings, so a reload /
  // late-join renders the same hidden/shown state.
  const iobDismissedAtMs = (() => {
    const v = settings?.iob_card_dismissed_at;
    if (!v) return null;
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : null;
  })();
  let newestContextMs = -Infinity;
  for (const b of boluses) newestContextMs = Math.max(newestContextMs, b.takenAt);
  for (const blk of blocks) {
    if (blk.kind === "meal") {
      for (const d of blk.doses)
        newestContextMs = Math.max(newestContextMs, d.takenAt);
    } else {
      newestContextMs = Math.max(newestContextMs, blk.takenAt);
    }
  }
  if (basalForCard) newestContextMs = Math.max(newestContextMs, basalForCard.takenAt);
  // Carbs-only meal blocks carry no dose timestamp, so fold in recent
  // carbs rows explicitly — a fresh carbs log must re-show the card.
  for (const t of treatments) {
    if (t.event_type === "carbs") {
      const ts = new Date(t.timestamp).getTime();
      if (now - ts <= IOB_DURATION_MS)
        newestContextMs = Math.max(newestContextMs, ts);
    }
  }
  const dueBasalActive = basalForCard != null && basalForCard.due;
  // Safety fix (2026-07-15): a dismissal must never hide the card while
  // there's real active rapid-acting insulin on board — dismissing right
  // after logging a dose (or shortly before one decays) left the card
  // hidden for the rest of its ~4h decay window, since nothing "newer"
  // ever arrived to auto-re-show it. Mirrors the existing due-basal
  // exception: `hasIOB` forces `iobCardDismissed` false exactly like
  // `dueBasalActive` does, so an active dose always keeps the card up.
  const iobCardDismissed =
    iobDismissedAtMs != null &&
    newestContextMs !== -Infinity &&
    newestContextMs <= iobDismissedAtMs &&
    !dueBasalActive &&
    !hasIOB;
  const showIobCard =
    (showRingsCard || basalForCard != null) && !iobCardDismissed;
  const dismissIobCard = () => {
    const iso = new Date().toISOString();
    // Optimistic local hide; the settings_update echo confirms it and
    // fans out to the other devices. A PATCH failure leaves the optimistic
    // hide (harmless — a new log or reload re-derives the shown state).
    if (settings)
      applyRemoteUpdate({ ...settings, iob_card_dismissed_at: iso });
    void patchUserSettings({ iob_card_dismissed_at: iso }).then((r) => {
      if (r.ok) applyRemoteUpdate(r.data);
    });
  };
  // ─── Cycle 451.1: Log while the alarm is sounding ──────────────
  // The return-to-Now rule would bounce a tap on Log straight back here,
  // leaving three visible buttons that do nothing. Instead they say what to
  // do about it. Clears itself the moment the audio stops, so once the alarm
  // is quiet Log behaves exactly as it always has.
  const [logBlocked, setLogBlocked] = useState(false);
  useEffect(() => {
    if (!alarmSounding) setLogBlocked(false);
  }, [alarmSounding]);
  const LOG_BLOCKED_LINE =
    "Quiet the alarm first: Snooze or I'm treating this.";
  const goToLog = (to: string): void => {
    if (alarmSounding) {
      // Cycle 452: Log opens OVER Now — no route change, so the pin never
      // fires and the alarm controls stay docked on top of it. The notice
      // below is the fallback for a mount with no overlay opener.
      if (onOpenLogOverlay) {
        onOpenLogOverlay(to);
        return;
      }
      setLogBlocked(true);
      return;
    }
    nav(to);
  };
  const onLogBasal = () => goToLog("/log?tab=insulin&basal=1");

  // Cycle 43: every active insulin row, newest first — the full set the
  // editor modal needs, NOT `activeBolusesForRings` (which caps at 2 for
  // the dual-ring visual). A 3-stacked correction must render all three.
  // Cycle 123: "active" is now kind-aware so the modal stays consistent
  // with the front card — a bolus is active inside the ~4h IOB window; a
  // basal is active for its full long-acting life (its
  // `insulin_duration_min`, default 1440), so a 24h Lantus logged this
  // morning still appears in the editor (and the header count) this
  // afternoon, matching the front-card BasalLine.
  const activeBolusTreatments = useMemo<Treatment[]>(() => {
    const bolusCutoff = now - IOB_DURATION_MS;
    return treatments
      .filter((t) => {
        if (t.event_type !== "insulin" || t.insulin_u == null) return false;
        const ts = new Date(t.timestamp).getTime();
        if (t.insulin_kind === "basal") {
          const durMs =
            (t.insulin_duration_min != null && t.insulin_duration_min > 0
              ? t.insulin_duration_min
              : DEFAULT_BASAL_DURATION_MIN) * 60_000;
          return ts >= now - durMs;
        }
        return ts >= bolusCutoff;
      })
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      );
  }, [treatments, now]);

  const chartRangeEnd = now;
  const chartRangeStart = useMemo(
    () => chartRangeEnd - chartHours * 60 * 60 * 1000,
    [chartRangeEnd, chartHours],
  );

  // Hero size:
  //  kiosk → clamp keyed off the hero-column width (cqw); two-digit
  //          values still ride the design's 224px max, three-digit
  //          values shrink to fit instead of overflowing the 1024×600
  //          card. The heroCol sets `container-type: inline-size`.
  //  web   → clamp(96px, 15vw, 200px); never grows past 200 on wide
  //          monitors and shrinks gracefully on narrow viewports.
  // Cycle 222.2: nudged down a little from the cycle-33/34.3 sizes — a
  // triple-digit value next to a DOUBLE-arrow glyph (`↑↑`/`↓↓`, two chars
  // wide) was crowding/overlapping the trend column at the old maxima. The
  // reduction is modest (~10–12%, mostly the max + the cqw/vw scale factor)
  // so two-digit values still read large across the kitchen.
  const heroFontSize: number | string =
    variant === "kiosk"
      ? "clamp(148px, 33cqw, 198px)"
      : variant === "mobile"
        ? "clamp(108px, 32vw, 164px)"
        : "clamp(92px, 13.5vw, 180px)";
  // Cycle 33 bumped the kiosk + web trend glyphs to 160 / clamp(80,
   // 10vw, 120). 34.3 trims them back down — the user found the
   // 160-px kiosk arrow too dominant and crowding the hero number.
   // Sized to sit comfortably alongside the hero value and centered
   // vertically with it (see `.heroRow` align-items below). Mobile
   // is unchanged — the cycle-30 phone hero already had the glyph at
   // a comfortable size.
  const trendSize: number | string =
    variant === "kiosk"
      ? 88
      : variant === "mobile"
        ? "clamp(36px, 11vw, 56px)"
        : // Cycle 297: web trend arrow bumped bigger.
          "clamp(56px, 7.5vw, 96px)";
  // Cycle 297: a heavier (thicker) trend arrow on web.
  const trendWeight = variant === "web" ? 700 : 500;

  const flavorClass =
    variant === "kiosk"
      ? styles.kiosk
      : variant === "mobile"
        ? styles.mobile
        : styles.web;
  // Cycle 34: keep the kiosk two-column layout (hero left, side
   // right) on BOTH branches. The cycle-34 .sparkPanelFill modifier
   // makes the chart panel stretch vertically inside the side column
   // — that's what fills the otherwise-empty space below
   // BolusRings (IOB-active) or below the window pills (IOB-empty).
   // An earlier cycle-34 single-column variant unmounted the hero
   // glucose reading on IOB-active; that's been reverted so the
   // hero number stays visible on every kiosk Now render.
  // Cycle 57.6: mobile + IOB is a special layout — the chart panel
  // collapses to a tappable "View Graph" card, and when the user
  // expands it the hero stays visible above (the chart fills the
  // space below the subline/delta down to the FooterTabs).
  const mobileIobActive = variant === "mobile" && hasIOB;
  const gridClass = `${styles.root} ${flavorClass} ${
    chartExpanded ? styles.chartExpanded : ""
  } ${mobileIobActive ? styles.iobActive : ""}`;

  // Cycle 34: measure the chart panel's available height on kiosk so
  // HistoryChart fills whatever space remains between the header,
  // BolusRings (when IOB-active), and FooterTabs nav. ResizeObserver
  // keeps the value in sync on font-size / chrome changes.
  // Cycle 46: the same measured-fill story now runs on mobile too so
  // the Now-screen chart panel stretches down to the FooterTabs nav
  // (the gate flipped from `!== "kiosk"` to `=== "web"`). Web alone
  // keeps its fixed numeric height (440).
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const [measuredChartH, setMeasuredChartH] = useState<number>(420);
  // Cycle 48: web normally keeps its fixed 440-px chart, but when the
  // chart is expanded it must fill the content area too, so the
  // measured-fill story now also runs on web while `chartExpanded`.
  // Cycle 114: `mobileLandscape` is in the deps so a rotation re-attaches
  // the observer to the landscape chart container (the portrait and
  // landscape subtrees mount different chart-container elements). The
  // mobile variant always runs the measured fill, landscape included.
  useLayoutEffect(() => {
    if (variant === "web" && !chartExpanded) return;
    const el = chartContainerRef.current;
    if (!el) return;
    const apply = () => {
      const h = Math.floor(el.clientHeight);
      if (h > 0) setMeasuredChartH(h);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
    // Cycle 122: `showIobCard` is in the deps so the observer re-attaches
    // when the portrait chart swaps between the pager's page 2 and the
    // chart-alone (no-card) layout — those are different DOM elements, so
    // the ref target changes and the old observation must be replaced.
  }, [variant, hasIOB, chartExpanded, mobileLandscape, showIobCard]);

  const chartHeight =
    variant === "kiosk" || variant === "mobile" || chartExpanded
      ? Math.max(160, measuredChartH)
      : 440;

  // Cycle 65: a single window set across kiosk / web / mobile —
  // 2h · 6h · 12h · 24h · 7d · 30d. The mobile-only [3, 6, 12]
  // collapsed trim and the cycle-48 mobile-expanded fork are both
  // gone. Mobile lets its pill row wrap or scroll to fit (six pills
  // × ~50 px ≈ 300 px < 390 px phone width). The 3-hour mobile pill
  // is retired.
  const windowChoices: readonly ChartHours[] = [2, 6, 12, 24, 168, 720];

  // Cycle 48: chevron toggle + pannable slice on mobile + web.
  // Cycle 50: kiosk joins the club. Widening this single gate
  // cascades automatically — the chevron render, the now-line, and
  // the tap-handler wiring all key off `canExpand`; the layout
  // modifier, `panSliceMs`, and the measured-fill effect all key off
  // `chartExpanded`. No other per-variant check is needed.
  const canExpand =
    variant === "mobile" || variant === "web" || variant === "kiosk";
  // Cycle 48: 12h/24h pan. Cycle 51: 7d/30d pan via PANNABLE_HOURS.
  // Cycle 65: short windows kept a per-variant fixed slice.
  // Cycle 183 (kiosk + web): open zoomed into HALF the selected window —
  // 6→3h / 12→6h / 24→12h — so a tapped pill lands on a readable slice
  // instead of a squashed full window. `2h` keeps its 0.65.2 universal
  // 1h slice; long windows (7d/30d) keep the 72h (3-day) default. The
  // MOBILE slice branch is unchanged (still 1h) so mobile Now stays
  // byte-identical.
  // 0.65.2: the 2h pill always uses a 1h slice on every variant so
  // dots render full-size and the hour is swipeable. Special-cased
  // first because a 2h-wide static plot is too dense even on the
  // 1024-px kiosk panel.
  const panSliceHours =
    chartHours === 2
      ? 1
      : isLongWindow(chartHours)
        ? LONG_WINDOW_DEFAULT_VISIBLE_HOURS
        : variant === "mobile"
          ? 1
          : chartHours / 2; // kiosk + web: half-window zoom-in
  // Cycle 57: kiosk doesn't gate zoom on the expand chevron.
  // 0.65.2: 2h is always pannable on every variant (no expand gate).
  // 0.65.5: mobile joins kiosk — pan/pinch always-on for every pannable
  // window on mobile Now too.
  // Cycle 183: WEB joins zoom-on-select too — the half-window slice is
  // live the instant a pill is tapped on web Now (no expand-chevron gate),
  // matching kiosk + the always-on 2h behavior. The chevron still toggles
  // the expanded HEIGHT/layout; only the slice is now always present.
  const zoomOnSelect =
    variant === "kiosk" || variant === "mobile" || variant === "web";
  const panSliceMs = PANNABLE_HOURS.includes(chartHours)
    ? chartHours === 2 || chartExpanded || zoomOnSelect
      ? panSliceHours * 60 * 60 * 1000
      : undefined
    : undefined;
  // Cycle 183: inertial flick on the kiosk + web Now charts only. Mobile
  // (and any default) passes nothing → byte-identical.
  const chartFlick = variant === "kiosk" || variant === "web";

  const expandToggle = canExpand ? (
    <button
      type="button"
      className={styles.expandBtn}
      aria-label={chartExpanded ? "Collapse chart" : "Expand chart"}
      aria-expanded={chartExpanded}
      onClick={() => setChartExpanded((v) => !v)}
    >
      {chartExpanded ? "▼" : "▲"}
    </button>
  ) : null;
  // Cycle 50: every supported variant now has `canExpand` true, so
  // all of them group the chevron + badge in `.sparkHeadRight`. The
  // bare-badge else branch is kept only as a safe fallback if a
  // future variant opts out of expand/collapse.
  const headRight = canExpand ? (
    <span className={styles.sparkHeadRight}>
      {expandToggle}
      <span className={`${styles.sparkBadge} tnum`}>live</span>
    </span>
  ) : (
    <span className={`${styles.sparkBadge} tnum`}>live</span>
  );

  // Cycle 50: the now-line + tap-a-dot now apply on kiosk too (it's in
  // `canExpand`). In collapsed mode the now-line is a hairline at the
  // extreme right edge (nowMs === rangeEndMs), exactly as it has been
  // on collapsed mobile/web since cycle 48 — consistent and intended.
  const chartShowNowLine = canExpand;
  const chartOnReadingTap = canExpand
    ? (r: Reading, anchorRect: DOMRect) =>
        setReadingDetail({ reading: r, anchorRect })
    : undefined;

  // ─── Cycle 116/117: mobile portrait helpers ──────────────────────
  const mobilePortrait = variant === "mobile" && !isLandscape;
  // The readings inside the visible window — drives the stats strip.
  const windowReadings = useMemo(
    () =>
      chartReadings.filter((r) => {
        const t = new Date(r.timestamp).getTime();
        return t >= chartRangeStart && t <= chartRangeEnd;
      }),
    [chartReadings, chartRangeStart, chartRangeEnd],
  );
  // Cycle 116 stats strip — TIR% / mean / LOW EPISODES over the windowed
  // series. Cycle 142 extracted the computation into the shared
  // `lib/glucoseStats.ts` so the portrait strip, the bare-state strip,
  // the trend overlay footer, and the landscape chart strip all read one
  // definition. Recomputes on window change.
  const portraitStats = useMemo(
    () => glucoseWindowStats(windowReadings, thresholds),
    [windowReadings, thresholds],
  );

  // ─── Cycle 142: bare-state plumbing ───────────────────────────────
  // The bare states (no rapid-acting IOB) replace the always-on chart
  // with a tappable stats strip that opens this overlay.
  const [trendSheetOpen, setTrendSheetOpen] = useState(false);
  // Cycle 391: the "I'm treating this" duration sheet. Local, transient
  // UI state only — the WINDOW itself lives on the server row, so this
  // never has to survive a reload or agree with another device.
  const [treatingSheetOpen, setTreatingSheetOpen] = useState(false);
  // ─── Cycle 456: "Who's treating?" ───────────────────────────────────
  // `pickerFor` is the window (`custody_opened_at`) the picker is up for, or
  // null. `pickerTapPending` latches that THIS device's tap succeeded; the
  // picker opens once the server frame carries the open window (which also
  // carries a linked account's attribution, so a linked phone never sees
  // the question).
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [pickerTapPending, setPickerTapPending] = useState(false);
  const alarmSoundingHere = useAlarmSoundingForPicker();
  const [treatingError, setTreatingError] = useState<string | null>(null);
  const [treatingBusy, setTreatingBusy] = useState(false);

  // Latches once the window is observed OPEN while the name step is up.
  const identityWindowSeenRef = useRef(false);

  // ⚠ CYCLE 404 — ONE TAP. The duration menu is gone: there is a single
  // ten-minute window and the SERVER assigns it, so this commits
  // immediately and then asks the optional name question.
  const beginTreating = useCallback(
    async () => {
      setTreatingBusy(true);
      const code = await episode.treat();
      setTreatingBusy(false);
      if (code) {
        // Stay open and say WHY. A dark sensor is the case that matters:
        // the alarm is still sounding and the reason is the feed, not the
        // button, so a generic failure here would train people to tap
        // again rather than look at the sensor.
        setTreatingError(treatingErrorMessage(code));
        return;
      }
      setTreatingError(null);
      // Cycle 456: this device's tap opened the window, so it may ask
      // "Who's treating?" once the server's frame shows the window open and
      // unattributed (see the picker effects below). The cycle-392 name
      // step stays dormant.
      setPickerTapPending(true);
    },
    [episode],
  );

  // ─── Cycle 404: "I can help after all" ────────────────────────────
  //
  // A hero who pressed 3 on an escalation call — "I can't help right now" —
  // is out of the CALL search for the rest of this episode (texts never
  // stop, and neither does the kiosk). This is the way back in, and it is
  // the only client surface that state has.
  //
  // ⚠ IT IS A SECONDARY ACTION AND IT MOVES NOTHING. It renders in its own
  // row BELOW the Snooze / "I'm treating this" cluster, so it cannot
  // displace, resize, reorder or re-label either of them, and it is below
  // the hero rather than over it. It is emphatically NOT a Stop by another
  // name: the cycle-73 urgent-low floor is untouched, because this changes
  // who gets RUNG and never whether the alarm sounds.
  //
  // ⚠ IT LIVES ON THE TAKEOVER ONLY, NOT ON THE COLLAPSED <UrgentBanner>.
  // That strip is a 40px single-line nowrap row whose one control is
  // Snooze; a second pill there squeezes it on a 375px phone, which is
  // exactly the displacement this action is forbidden to cause. The
  // takeover is also the surface a hero actually finds their phone on
  // during a live, sounding urgent low.
  const optOutLookupEnabled =
    variant !== "kiosk" &&
    episode.isLive &&
    episode.episodeLevel === "urgent_low";
  const myContactId = useMyLinkedContactId(optOutLookupEnabled, userId);

  // Cycle 456: open the picker for this window once the server shows it
  // open and unattributed, if this device's own tap opened it. A kiosk is
  // never linked; a linked phone account is attributed by the server and
  // asked nothing.
  // ⚠ The latch asks about the window THIS device's own successful tap
  // opened (`episode.lastTreatWindow()`, from the POST response) and no
  // other (astra reviews, rounds 1 and 2): a missed frame, a window that
  // closes while audio holds the question, or a window somebody else opens
  // later never gets the question from this device. `pickerTapSeenRef`
  // records that the latch has seen its window, so a later close drops it
  // instead of waiting for frames.
  const pickerTapSeenRef = useRef(false);
  useEffect(() => {
    if (!pickerTapPending) {
      pickerTapSeenRef.current = false;
      return;
    }
    const step = pickerLatchStep({
      tapWindow: episode.lastTreatWindow(),
      seen: pickerTapSeenRef.current,
      declaredOpen: episode.treating,
      windowId: episode.custodyOpenedAt,
      alarmSounding: alarmSoundingHere,
    });
    if (step === "drop") {
      setPickerTapPending(false);
      return;
    }
    if (step === "wait") return;
    pickerTapSeenRef.current = true;
    // Never before audio has stopped.
    if (step === "seen-wait") return;
    setPickerTapPending(false);
    if (
      pickerMayOpen({
        declaredOpen: episode.treating,
        windowId: episode.custodyOpenedAt,
        treatingBy: episode.treatingBy,
        tappedHere: true,
        linkedHere: variant !== "kiosk" && myContactId != null,
        alarmSounding: alarmSoundingHere,
        feedDark: current == null,
        ascentLatched: episode.ascentLatched,
      })
    ) {
      setPickerFor(episode.custodyOpenedAt);
    }
  }, [
    pickerTapPending,
    episode.treating,
    episode.custodyOpenedAt,
    episode.treatingBy,
    variant,
    myContactId,
    alarmSoundingHere,
    current,
    episode.ascentLatched,
  ]);

  // …and close it the moment the window closes or changes, a prompt comes
  // up, or any alarm audio starts on this device.
  useEffect(() => {
    if (pickerFor == null) return;
    if (
      pickerMustClose({
        openedFor: pickerFor,
        declaredOpen: episode.treating,
        windowId: episode.custodyOpenedAt,
        alarmSounding: alarmSoundingHere,
        promptPending: episode.checkinPending,
        feedDark: current == null,
        ascentLatched: episode.ascentLatched,
      })
    ) {
      setPickerFor(null);
    }
  }, [
    pickerFor,
    episode.treating,
    episode.custodyOpenedAt,
    alarmSoundingHere,
    episode.checkinPending,
    current,
    episode.ascentLatched,
  ]);

  // A tap whose window never shows up (refused, or the episode ended)
  // must not leave a latch that opens a picker on some later window.
  useEffect(() => {
    if (!episode.isLive) {
      setPickerTapPending(false);
      setPickerFor(null);
    }
  }, [episode.isLive]);
  const [optOutUndoBusy, setOptOutUndoBusy] = useState(false);
  // Optimistic hide, KEYED TO THE ALERT ROW it was taken against. There is
  // no client path to push a refreshed AlertOut into the socket cache (the
  // cache is fed by WS frames only), so the response's `episode_opt_outs`
  // cannot be written back directly; this holds the action hidden until the
  // next `alert_heartbeat` reconciles the row. Keying it by alert id means
  // a NEW episode starts clean — an opt-out is episode-scoped, and so is
  // its undo.
  const [optOutUndoneFor, setOptOutUndoneFor] = useState<string | null>(null);

  const undoOptOut = useCallback(async () => {
    const id = episode.alertId;
    if (!id) return;
    setOptOutUndoBusy(true);
    try {
      // ⚠ A FAILURE SAYS NOTHING ON SCREEN. This sits on top of a sounding
      // urgent low, where the only messages that earn space are the ones
      // about the alarm itself (the treating refusal below is the whole
      // list). The action simply stays put so it can be tapped again.
      const r = await alertsApi.undoOptOut(id);
      if (r.ok) {
        setOptOutUndoneFor(id);
      } else {
        console.debug("opt-out undo not recorded", r.error?.status);
      }
    } catch {
      /* same — the action stays available, nothing is surfaced */
    } finally {
      setOptOutUndoBusy(false);
    }
  }, [episode.alertId]);

  const showOptOutUndo =
    optOutUndoVisible({
      level: episode.episodeLevel,
      isLive: episode.isLive,
      variant,
      myContactId,
      episodeOptOuts: episode.episodeOptOuts,
    }) &&
    // The optimistic hide only counts against the row it was taken on.
    !(optOutUndoneFor !== null && optOutUndoneFor === episode.alertId);

  // The sheet can never outlive the thing it is choosing a duration for.
  useEffect(() => {
    if (!episode.isLive || episode.episodeLevel !== "urgent_low") {
      setTreatingSheetOpen(false);
    }
  }, [episode.isLive, episode.episodeLevel]);

  // ⚠ THE NAME STEP MUST NEVER STAND BETWEEN A CAREGIVER AND A RE-ARMED
  // ALARM. It is explicitly optional ("the window is already running
  // either way"), so people walk away from it — and its scrim covers the
  // alarm surface. If a breakthrough then tears the window down and the
  // full urgent-low alarm returns, Snooze would sit dimmed behind an
  // unattended name prompt, visible but not tappable. That breaks the
  // cycle-63/81 floor: a control must be REACHABLE whenever audio can
  // sound, not merely on screen.
  //
  // So the step closes itself the instant the window ends.
  //
  // ⚠ AND IT MUST NOT CLOSE BEFORE THE WINDOW HAS BEEN *SEEN* OPEN.
  // On a 2nd/3rd tap `treatingUntilMs` prefers server truth once
  // `treatingCount > 0`, so `treating` is still FALSE for the round-trip
  // between the POST resolving and the `alert_treating` frame landing — a
  // naive `!treating` close fires in that gap and dismisses the name step
  // instantly. It fails safe (closed), but it silently deletes the feature
  // on exactly the repeat taps a household is most anxious about. So the
  // close arms only after we have observed the window actually open for
  // THIS step; the self-dismiss belt below still covers a window that
  // never opens at all.
  useEffect(() => {
    if (!treatingSheetOpen) {
      identityWindowSeenRef.current = false;
      return;
    }
    if (episode.treating) {
      identityWindowSeenRef.current = true;
      return;
    }
    if (identityWindowSeenRef.current) {
      setTreatingSheetOpen(false);
    }
  }, [treatingSheetOpen, episode.treating]);

  // And it self-dismisses, because an optional prompt left up over a live
  // alarm is a prompt nobody asked to keep.
  useEffect(() => {
    if (!treatingSheetOpen) return;
    const id = window.setTimeout(
      () => setTreatingSheetOpen(false),
      TREATING_IDENTITY_AUTO_DISMISS_MS,
    );
    return () => window.clearTimeout(id);
  }, [treatingSheetOpen]);
  // Safety invariant: an alarm control must never sit behind the
  // overlay's scrim. Any live episode force-closes the sheet (the
  // urgent takeover additionally unmounts this whole return).
  useEffect(() => {
    if (episode.isLive) setTrendSheetOpen(false);
  }, [episode.isLive]);

  // ─── Alarm mode ─────────────────────────────────────────────────
  // Cycle 20.x: when an alarm episode is active the Now screen IS
  // the alarm UI.
  // Cycle 33: urgent-only takeover. A regular `low` episode keeps the
  // normal hero layout and renders <LowAlarmBanner> above the grid
  // (see the bottom of this function); only `urgent_low` /
  // `urgent_high` still bring up the flashing-red full-screen view.
  // Cycle 42 + cycle 61 + cycle 62: an urgent episode collapses out
  // of the full-screen takeover into the thin <UrgentBanner> once
  // the parent has pressed Snooze (`hasSnoozed`) or while the
  // snooze countdown runs — the Now screen below stays visible so
  // the correction can be watched.
  // Cycle 81: the inline full-screen urgent view mounts off the same
  // phase-live + persisted-episode-level signal as the banners (NOT
  // the live reading's instantaneous zone), so the takeover stays up
  // through the urgent recovery-dwell window — when the reading has
  // climbed back across the threshold (`episode.level` null) but the
  // episode is still live — instead of dropping to a control-less
  // screen. A more-severe live reading still wins via `episodeLevel`
  // (so a same-side mild→urgent re-arm escalates the surface).
  // Cycle 187: **Stop** now collapses the takeover too. Through cycle
  // 62/67 pressing Stop held the full screen in a muted treatment
  // (acutely-dangerous urgents weren't silenced AND visually dismissed
  // at once); this cycle supersedes that for the urgent family so a
  // Stopped urgent minimizes to the same thin <UrgentBanner> as a
  // Snoozed one (the banner carries the muted "Stopped" state). Only
  // an ACTIVE (sounding, not-yet-addressed) urgent still holds the
  // full screen; a worsening re-arm re-expands it (episodeLevel flips
  // back to the live urgent zone, `stopped`/`hasSnoozed` cleared). The
  // cycle-73 `urgent_low` floor stands — `stop()` no-ops there, so
  // `episode.stopped` is only ever true for `urgent_high`.
  // ⚠ CYCLE 454 (operator decision): URGENT LOW IS THE ONLY FULL-SCREEN
  // TAKEOVER. Urgent High left both gates below and renders in
  // `HighAlarmBanner` (Stop only), like High. Everything said here about
  // the urgent family now describes urgent low.
  const urgentCollapsed =
    episode.episodeLevel === "urgent_low" &&
    episode.isLive &&
    // Cycle 391: an open treating window collapses the takeover to the
    // banner too, following the cycle-61 collapse-on-acknowledgement
    // precedent — the declaration IS an acknowledgement, and the room
    // needs the Now screen back to watch the correction land. The banner
    // keeps the live value and the countdown, so the alarm never leaves
    // the screen; it stops OWNING the screen.
    // ⚠ CYCLE 416: A FAILED ACK KEEPS THE TAKEOVER OPEN. Without this term
    // the urgent family loses the failure notice in exactly the dangerous
    // case: a network fault KEEPS the optimistic flip, so `hasSnoozed` /
    // `stopped` go true, the takeover collapses to `UrgentBanner` — and the
    // takeover is the ONLY place `<AckFailureNotice/>` mounts. The result was
    // "Urgent Low · Snoozing 4:52" with no control and no hint, which is the
    // precise defect this cycle exists to abolish, surviving on the most
    // dangerous level. The collapse is an acknowledgement-collapse; an ack
    // that never landed is not an acknowledgement.
    episode.ackFailure == null &&
    (episode.hasSnoozed ||
      episode.phase === "snoozed" ||
      episode.stopped ||
      episode.treating);
  // ⚠ CYCLE 398: THE URGENT FULL-SCREEN TAKEOVER NOW CLEARS ON THE FIRST
  // IN-RANGE READING TOO.
  //
  // Cycle 304 deliberately left this surface alone: its predicate described
  // only MUTED episodes, and this view renders only for ACTIVE ones, so the
  // gate could never have fired here. Cycle 398 widened the predicate to
  // active episodes — and leaving the takeover up would be the stale
  // FULL-SCREEN version of the exact bug being fixed (a red "URGENT LOW"
  // covering the whole tablet while the household is already back in
  // range).
  //
  // No control is lost. `audioOn` is derived from the same predicate, so
  // the loop is already silent in this render — the cycle-81 floor asks for
  // a control whenever audio COULD sound, and audio cannot.
  const isUrgentEpisode =
    episode.isLive &&
    episode.episodeLevel === "urgent_low" &&
    !urgentCollapsed &&
    !episode.bannerSuppressedInRange;
  // Cycle 407: the hand-back. When a treating window breaks, the full
  // alarm returns with a one-line reason for a short while — never a
  // silent swap. Derived from the teardown frame's reason + the live value
  // at that moment; ends that re-armed nothing (recovery) map to no line.
  // ── Cycle 447: the custody question, on the surfaces that are not the
  // treating screen ──────────────────────────────────────────────────
  // The server authors the prompt on the row and resolves an unanswered
  // deadline itself (cycle 427), but <TreatingScreen> — the only surface
  // that could render it — is mounted ONLY while `treating || quietWindow`.
  // The moment a window ends, the surface on screen becomes the inline
  // takeover or the collapsed <UrgentBanner>, and a live question had
  // nowhere to go.
  //
  // ⚠ THE QUESTION RENDERS WITH ITS OWN ACTIONS, OUT OF FLOW, AND NOT IN
  // THE SUBTEXT SLOT. Taking the cycle-407 hand-back slot was tried first
  // and MEASURED WRONG: this surface is vertically centred, and the
  // question ("Is everything OK with Sam?" — Sam is the walkthrough's
  // declared fictional stand-in, never a real member — one line, 21 px) is SHORTER
  // than the advisory it replaced ("Treat now. Eat or drink fast-acting
  // carbs.", two lines, 42 px), so the column shrank and the whole face —
  // number, level pill, Snooze — moved up 10.5 px on a 390-wide phone.
  // Leaving the column completely untouched is the only shape that makes
  // the alarm's geometry identical with and without a question.
  // The block mounts where there is something to answer OR a window to
  // leave. On a bare live alarm with no window there is nothing to resume
  // from, and a control that can only be refused is the shape cycle 427
  // removed — so it is absent there rather than present-and-refused.
  const showCustodyPrompt =
    episode.checkinPending || episode.treating || episode.quietWindow;
  const handBack = episode.custodyHandBack;
  const handBackLine =
    handBack != null &&
    now - handBack.atMs < HANDBACK_LINE_MS &&
    handBack.alertId === episode.alertId
      ? custodyHandBackLine(
          handBack.reason,
          activeMember?.display_name,
          handBack.sgv != null ? displayValue(handBack.sgv, units) : null,
        )
      : null;

  // Cycle 407: the treating screen — the alarm TRANSFORMS into it while
  // DECLARED custody is open (`episode.treating` is declared-only; since
  // cycle 409 a snooze mounts it too, via `episode.quietWindow`, with
  // honest copy). It replaces the takeover this early-return sits next
  // to, so the transition is one render, never a blank frame. Everything it
  // shows is the ACTIVE member's: a second member's alarm is never gated by
  // this window.
  // Cycle 409: …and while a QUIET window (a snooze on an urgent low) is
  // open, on every device. Same ring, same two loud actions, honest copy
  // ("Snoozed", never "treating"), plus "I'm treating this" to convert it.
  // `episode.treating` is still declared-only: the audio gate, the check-in
  // and the inference read that, not this mount.
  // ⚠ CYCLE 436 — THE PREDICATE IS THE DECLARED WINDOW, AND NOTHING ELSE.
  // Cycle 412 added a `!treatingSheetOpen` term here so the cycle-392 identity
  // sheet could render inside the takeover below. It could not: the sheet's
  // ONLY mount sits inside `if (isUrgentEpisode)`, and `urgentCollapsed`
  // carries `episode.treating` (cycle 391), so the takeover branch is dead in
  // exactly the renders the sheet flag is set. The two conditions were
  // mutually exclusive BY CONSTRUCTION — `setTreatingSheetOpen(true)` fires
  // only after `episode.treat()` resolves, which is to say only once the
  // optimistic window is already open. So a successful tap put the screen in a
  // state where NEITHER surface mounted, and the caregiver who had just
  // claimed the episode was handed back the ordinary Now screen with a red
  // banner and a Snooze button. It lasted exactly
  // `TREATING_IDENTITY_AUTO_DISMISS_MS` — 45 s — because the sheet's own
  // self-dismiss was the only thing that ever closed the hole.
  //
  // A sheet is a thing drawn OVER a screen. It was never this screen's
  // business whether one is open, and a declared window must never be a screen
  // without its timer. The identity step stays dormant (cycle 436 option 3 —
  // see the `<TreatingSheet>` mount below), so removing the term changes
  // nothing about what the sheet does; it changes what happens when the sheet
  // does nothing.
  // ⚠ CYCLE 451 — THE MOUNT GATE MOVED INTO `lib/treating.ts` UNCHANGED,
  // AND IT IS NOW READ BY TWO CALLERS. This early return is what puts the
  // treating screen in front of the bedside clock face, and `App.tsx`'s
  // chrome decision has to know that happened: while the face is believed
  // to be on screen the app chrome is suppressed, so from cycle 407 until
  // this one a declared window inside a scheduled bedside window painted
  // the treating screen with no header and no nav. Both callers now ask
  // `treatingSurfaceMounted(episode)` — the same function, not a second
  // copy of the condition.
  if (treatingSurfaceMounted(episode)) {
    return (
      <TreatingScreen
        variant={variant}
        memberName={activeMember?.display_name ?? null}
        current={current}
        ageMinutes={view.reading ? view.ageMinutes : null}
        units={units}
        thresholds={thresholds}
        episode={episode}
        now={now}
        speak={speak}
        onDeclare={() => {
          setTreatingError(null);
          void beginTreating();
        }}
        declareBusy={treatingBusy}
        declareError={treatingError}
        /* Cycle 437: the insulin-on-board gauge's data — the same
           `activeBolusesForRings` / `basalForContext` derivation the Now
           card reads above, never a second one. `boluses` already carries
           each dose's ring colour from the cycle-118 id match.

           ⚠ DELIBERATELY `basal`, NOT `basalForCard`. They differ in
           exactly one case and it is the case that matters here: a DUE
           basal whose cycle-163.1 nudge was dismissed makes `basalForCard`
           null, which would drop the third ring off this gauge entirely.
           That dismissal is a device-local convenience on the Now card —
           it must not delete a lapsed long-acting dose from a live
           urgent-low surface, where it is exactly what a caregiver
           deciding how to treat needs to see. Pinned in
           treatingInstruments.test.ts so a later tidy-up cannot align
           them the wrong way. */
        boluses={boluses}
        basal={basal}
        treatingBy={episode.treatingBy}
        selfTreating={isSelfTreating(episode.treatingBy, myContactId)}
        onNotTreater={
          variant !== "mobile" && episode.custodyOpenedAt != null && !alarmSoundingHere
            ? () => setPickerFor(episode.custodyOpenedAt)
            : undefined
        }
        picker={
          pickerFor != null &&
          episode.treating &&
          pickerFor === episode.custodyOpenedAt &&
          activeMember?.id ? (
            <WhoTreatingSheet
              variant={variant === "kiosk" ? "kiosk" : variant === "web" ? "web" : "mobile"}
              landscape={variant === "mobile" && isLandscape}
              memberId={activeMember.id}
              memberName={activeMember.display_name ?? null}
              windowId={pickerFor}
              secondsTotal={episode.attributionPickerSeconds}
              timerText={fmtTreatingRemaining(episode.treatingRemainingMs ?? 0)}
              timerFraction={ringFraction(episode.treatingRemainingMs ?? 0, ringTotalMs())}
              iob={iobChipEntries(boluses, basal, now)}
              current={episode.treatingBy}
              onClose={() => setPickerFor(null)}
            />
          ) : undefined
        }
      />
    );
  }

  // ─── Cycle 410: the lead window's takeover ───────────────────────────
  // While a LEAD WINDOW is open on the active member's urgent low — the
  // phone calls are running and this device holds the urgent audio UPGRADE
  // until the deadline on its own clock — the takeover renders as the lead
  // screen: flashing, counting down, "I've got the member" through the SAME
  // `beginTreating` door, "Sound the alarm now", and Snooze if and only if
  // audio is sounding for this member (the descent). It sits BELOW the
  // treating/quiet mount on purpose: custody of either kind closes the
  // window server-side on the same transaction, and locally the custody
  // surfaces already own the quiet. The instant `episode.audioLead` falls
  // — deadline, teardown frame, sound-now — the ordinary takeover below
  // renders again with Snooze back on screen. Display + audio only: no
  // gate, no rail, no slot reads this mount.
  if (
    episode.isLive &&
    episode.audioLead &&
    episode.episodeLevel === "urgent_low" &&
    !episode.treating &&
    !episode.quietWindow &&
    // The `urgentCollapsed` terms too: unreachable with unified custody on
    // (an urgent-low snooze opens quiet custody, which wins above), but
    // under the revert switch a plain snooze must collapse to the banner
    // exactly as it does for the ordinary takeover.
    !episode.hasSnoozed &&
    episode.phase !== "snoozed" &&
    !episode.bannerSuppressedInRange
  ) {
    return (
      <AudioLeadScreen
        variant={variant}
        memberName={activeMember?.display_name ?? null}
        current={current}
        ageMinutes={view.reading ? view.ageMinutes : null}
        units={units}
        thresholds={thresholds}
        episode={episode}
        now={now}
        onDeclare={() => {
          setTreatingError(null);
          void beginTreating();
        }}
        declareBusy={treatingBusy}
        declareError={treatingError}
        onSoundNow={() => void episode.soundNow()}
        onSnooze={() => void episode.address()}
      />
    );
  }

  if (isUrgentEpisode) {
    // Cycle 187: a Stopped urgent now collapses to <UrgentBanner>
    // (see `urgentCollapsed` above), so this full-screen view is only
    // reached for an ACTIVE / snooze-elapsed urgent — `isStopped` is
    // inert here now. The stopped-state JSX below is kept as harmless
    // defensive rendering in case a future change ever routes a
    // stopped urgent back through the takeover.
    const isStopped = episode.stopped;
    // Cycle 428 — v3 "Coming back up", TEXT ONLY: while the urgent-low row
    // is still open and the latest fresh reading is ABOVE the urgent-low
    // line (the whole climb, band included), the header carries the v3
    // sentence so a caregiver knows WHY the takeover is still up and the
    // low rung is quiet. Same ring, same buttons, same sound state as the
    // reading before — the tone goes on until the SERVER recovers (two
    // consecutive fresh readings at or above the low line).
    const recovering = episode.recoveringFromUrgentLow;
    const alarmTone =
      (recovering
        ? recoveringHeaderLine(activeMember?.display_name)
        : episode.episodeLevel === "urgent_low"
          ? "URGENT LOW"
          : "URGENT HIGH") + (isStopped ? " · STOPPED" : "");
    const alarmIsLow = episode.episodeLevel === "urgent_low";
    // Cycle 424: while the member climbs out of an urgent low the low rung
    // is latched off server-side; this line SAYS SO in the subtext's own
    // slot (the cycle-407 hand-back precedent — copy only, no layout
    // change), because a rail that goes quiet without saying why is worse
    // than one that stays loud (cycle 392).
    // Cycle 428: when the header already carries the Stage-6 sentence the
    // subtext returns to the treat guidance instead of saying it twice.
    const alarmSubtext = isStopped
      ? "Stopped — will re-alarm if it gets worse."
      : episode.ascentLatched && !recovering
        ? ascentHoldLine(activeMember?.display_name)
        : alarmIsLow
          ? "Treat now. Eat or drink fast-acting carbs."
          : "Check ketones. Hydrate. Consider correction.";
    // Cycle 62: Snooze + Stop replace Address + Silence. In the
    // muted (stopped) state only Snooze is offered — Stop is
    // already pressed by definition.
    const showActiveButtons =
      episode.phase === "active" && !episode.hasSnoozed && !isStopped;
    const showStoppedButtons = isStopped;
    const showSnooze = episode.phase === "snoozed";
    const sgvDisplay =
      episode.sgv != null ? displayValue(episode.sgv, units) : "—";
    // Cycle 427: No Data mode — a rendering state from the reading the
    // frames carry (the same derivation the normal face uses below).
    const takeoverNoData = current == null;

    return (
      <div className={`${styles.root} ${styles.alarm} ${
        variant === "kiosk" ? styles.alarmKiosk : styles.alarmWeb
      }${treatingSheetOpen ? ` ${styles.alarmSheetOpen}` : ""}`}>
        <div className={styles.alarmContent}>
          <div
            className={`${styles.alarmPill}${recovering ? ` ${styles.alarmPillLong}` : ""}`}
          >
            <span className={styles.alarmPillDot} />
            {alarmTone}
          </div>
          <div className={styles.alarmMember}>
            {activeMember?.display_name ?? "—"}
          </div>
          {/* Cycle 427: No Data mode on the takeover — the number greys
              and its age is shown (the meta row below); both buttons stay.
              A rendering state from the reading the frames carry. */}
          {takeoverNoData ? (
            <div
              className={`${styles.alarmHero} ${styles.alarmHeroNoData}`}
              data-testid="takeover-no-data"
            >
              No data
            </div>
          ) : (
            <div className={`${styles.alarmHero} tnum`}>{sgvDisplay}</div>
          )}
          <div className={styles.alarmMeta}>
            <span className="tnum">{fmtAge(ageMin)}</span>
            {deltaMgdl !== null && (
              <>
                <span className={styles.alarmMetaDot} />
                <span className="tnum">
                  {deltaMgdl >= 0 ? "+" : ""}
                  {units === "mmol/L"
                    ? toMmol(deltaMgdl).toFixed(1)
                    : deltaMgdl}{" "}
                  {units}
                </span>
              </>
            )}
          </div>
          {/* Cycle 407: the hand-back reason line takes the subtext's
              OWN slot for 30 s — it replaces the advisory sentence rather
              than adding a row, so the takeover's measured geometry (which
              the cycle-395 harness found already tight with a sheet open)
              is byte-identical whether or not a line is showing.

              ⚠ CYCLE 447 — THE MISSING HALF OF THAT PRECEDENT: TAKING THIS
              SLOT ONLY KEEPS THE GEOMETRY IF THE REPLACEMENT OCCUPIES THE
              SAME NUMBER OF LINES. This surface is vertically centred
              (`.alarm` is `align-items: center`), so the column's height
              IS the layout. Cycle 447 put the custody question here and
              measured the face move UP 10.5 px on a 390-wide phone: the
              question ("Is everything OK with Sam?", ONE line, 21 px) is
              SHORTER than the advisory it replaced ("Treat now. Eat or
              drink fast-acting carbs.", TWO lines, 42 px), so the column
              shrank by 21 px and the centring gave half of it back at the
              top. The hand-back line happens to wrap the same way the
              advisory does, which is why cycle 407 never saw this.

              The question therefore does NOT live here — it renders with
              its own actions, out of flow, at the bottom of the surface.
              Anything a future cycle wants to put in this slot must be
              line-for-line the same height as `alarmSubtext`, or measured
              and proven not to move the action row. */}
          <div
            className={styles.alarmSubtext}
            data-testid={handBackLine ? "custody-handback" : undefined}
            role={handBackLine ? "status" : undefined}
            style={handBackLine ? { fontWeight: 800, opacity: 1 } : undefined}
          >
            {handBackLine ?? alarmSubtext}
          </div>

          {showActiveButtons && (
            <div
              style={{
                display: "inline-flex",
                gap: 16,
                flexWrap: "wrap",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <button
                type="button"
                onClick={() => void episode.address()}
                className={styles.alarmBtn}
              >
                Snooze
              </button>
              {/* Cycle 391: on urgent low ONLY, Snooze gains a partner —
                  "I'm treating this". It is NOT Stop and it never
                  becomes Stop: it opens a bounded, self-expiring,
                  breakable window. `Stop` stays absent here exactly as
                  the cycle-73 floor requires. */}
              {alarmIsLow && (
                <button
                  type="button"
                  onClick={() => {
                    setTreatingError(null);
                    void beginTreating();
                  }}
                  className={styles.alarmBtn}
                  disabled={treatingBusy}
                  style={{
                    background: "transparent",
                    color: "#fff",
                    border: "2px solid #fff",
                    boxShadow: "none",
                  }}
                >
                  {treatingBusy ? "Starting\u2026" : "I'm treating this"}
                </button>
              )}
              {/* Cycle 73: Stop is hidden on urgent_low here too.
                  This inline urgent view is the second surface (after
                  <UrgentOverlay/>) that renders the full-screen red
                  takeover; the two must stay in lockstep so neither
                  shows a Stop button for a hypoglycemic event. */}
              {!alarmIsLow && (
                <button
                  type="button"
                  onClick={episode.stop}
                  className={styles.alarmBtn}
                  style={{
                    background: "transparent",
                    color: "#fff",
                    border: "2px solid #fff",
                    boxShadow: "none",
                  }}
                >
                  Stop
                </button>
              )}
            </div>
          )}
          {/* ⚠ CYCLE 404 — THE REFUSAL NEEDS A HOME NOW THAT THE SHEET
              DOES NOT OPEN FIRST. It used to render inside the duration
              step; with one tap there is no sheet to hold it, and the one
              refusal that MUST reach the caregiver is the dark sensor:
              the alarm is still sounding and the reason is the feed, not
              the button. Dropping this would train people to tap again
              instead of looking at the CGM. */}
          {/* Cycle 427: the cap line that stood here is gone with the cap
              (operator-authorized 2026-09-08): every window is ten minutes,
              so there is no shortened window to warn about. */}
          {treatingError && (
            <div className={styles.alarmSubtext} role="alert">
              {treatingError}
            </div>
          )}
          {/* ⚠ CYCLE 404 — "I can help after all". The SECONDARY row, and
              it stays secondary: its own line below the primary cluster, a
              text affordance rather than a pill, and nothing above it moves
              whether it is here or not. It is gated on `variant !== "kiosk"`
              inside `optOutUndoVisible` — the kiosk signs in as the
              household owner, so an id can match on a shared tablet and only
              that guard stands between a bedside panel and one person's
              private "put me back on the call list". */}
          {showOptOutUndo && (
            <div style={{ marginTop: 14 }}>
              <button
                type="button"
                onClick={() => void undoOptOut()}
                disabled={optOutUndoBusy}
                style={{
                  appearance: "none",
                  border: 0,
                  background: "transparent",
                  color: "#fff",
                  font: "inherit",
                  fontSize: 15,
                  fontWeight: 700,
                  letterSpacing: "0.01em",
                  textDecoration: "underline",
                  textUnderlineOffset: 4,
                  // Comfortable target without the pill's visual weight —
                  // it must never read as a third primary control.
                  padding: "10px 16px",
                  minHeight: 44,
                  opacity: optOutUndoBusy ? 0.6 : 0.92,
                  cursor: optOutUndoBusy ? "default" : "pointer",
                  fontFamily: "inherit",
                }}
              >
                {optOutUndoBusy ? "Adding you back\u2026" : OPT_OUT_UNDO_LABEL}
              </button>
            </div>
          )}
          {/* Cycle 73: stopped-state branch is reachable only from
              urgent_high after this cycle (urgent_low cannot be
              stopped). */}
          {showStoppedButtons && !alarmIsLow && (
            <button
              type="button"
              onClick={() => void episode.address()}
              className={styles.alarmBtn}
              aria-label="Snooze (audio paused; re-arm on worse crossing)"
            >
              Snooze
            </button>
          )}
          {/* ⚠ Cycle 416: a failed ack outranks the snoozed presentation
              here exactly as it does on the banners. The notice below carries
              its own Retry, so a control is on screen in this branch too. */}
          {showSnooze && !episode.ackFailure && (
            <div className={styles.alarmSnooze}>
              <span className={styles.alarmSnoozeLabel}>Snoozing</span>
              <span className={`${styles.alarmSnoozeTimer} tnum`}>
                {fmtSnoozeNow(episode.snoozeRemainingMs ?? 0)}
              </span>
              <span className={styles.alarmSnoozeHint}>
                Alarm resumes if {alarmIsLow ? "still low" : "still high"}
              </span>
            </div>
          )}
          {/* ── Cycle 447: the custody question ────────────────────────
              LAST in `alarmContent`, BELOW every existing control, and
              OUT OF FLOW (`position: absolute` at the bottom of `.alarm`,
              see CustodyPrompt.module.css), so the rows above keep their
              measured top whether or not a question is pending. The block
              renders the question text AND the actions — the subtext slot
              above is untouched (see the gate comment on
              `showCustodyPrompt` for why the slot was measured wrong).

              ⚠ Stop is not here and never will be — the cycle-73 floor.
              "I need help now" and "Resume alerts" can only make things
              louder, so they stand whether or not a question is pending;
              "Yes"/"No" answer a declaration and are absent on a quiet
              window rather than present-and-refused. */}
          {alarmIsLow && showCustodyPrompt && (
            <CustodyPrompt
              episode={episode}
              memberName={activeMember?.display_name ?? null}
              nowMs={now}
              tone="takeover"
              hidden={treatingSheetOpen}
            />
          )}
        </div>
        {/* ⚠ Cycle 416: the full sentence, on the surface with room for it.
            Never a toast — a caregiver who missed it would go on believing
            the alarm was handled. */}
        <AckFailureNotice
          failure={episode.ackFailure}
          onRetry={episode.retryAck}
        />
        {/* Cycle 391: the sheet renders INSIDE the alarm surface, so the
            dimmed urgent context — the live value, the trend, the red
            treatment — stays on screen behind it. The room must not lose
            sight of the number while somebody picks a duration.

            ⚠ CYCLE 436 — THIS MOUNT IS DORMANT, AND IT IS KEPT ON PURPOSE.
            On a FIRST declaration it is unreachable, and has been since
            cycle 404 made the tap a single commit: `setTreatingSheetOpen`
            fires only AFTER the window is open, and an open window collapses
            the takeover (`urgentCollapsed` carries `episode.treating`), so
            this branch does not run in any render where the flag is set.
            Cycle 407's early return added a second, redundant blocker; cycle
            412 removed only that one.

            ⚠ CYCLE 456 RETIRED ITS LAST OPENER. `beginTreating` no longer
            sets `treatingSheetOpen` at all — the "Who's treating?" picker
            (`WhoTreatingSheet`, drawn inside the treating screen) asks the
            question now — so this mount is unreachable from this bundle. It
            is kept, with its safety effects, until the cycle-392 identity
            endpoint is removed for older clients. The paragraph below is the
            pre-456 history.

            ⚠ (PRE-456) IT IS NOT *STRICTLY* UNREACHABLE, AND THE EXCEPTION IS WORTH
            KNOWING. On a 2nd/3rd tap `treatingUntilMs` prefers server truth
            once `custody_count > 0`, so `treating` stays FALSE for the round
            trip between the POST resolving and the `alert_treating` frame
            landing — the same gap the effect above is armed against. In
            those few renders the early return does not fire, the takeover
            branch runs, and the sheet CAN mount. That path is byte-identical
            before and after this cycle; it is narrow, transient, and the
            reason the sheet's safety effects (auto-close on window end, 45 s
            self-dismiss) must stay wired.

            **Nothing shipped is being removed here.** In practice the
            identity step has produced no by-name household notice since
            cycle 404, so leaving it dormant changes no live behaviour — a
            deferral, not a deletion, and reviving it (the hero list, the
            pick handler, the by-name notice) is its own cycle. Do not delete
            this mount, its props, or `TREATING_IDENTITY_AUTO_DISMISS_MS` to
            satisfy a dead-code check; report the check instead. */}
        {treatingSheetOpen && alarmIsLow && (
          <TreatingSheet
            onIdentity={(contactId: string | null) => {
              // Optimistic: close immediately. The confirmation arrives
              // over `alert_treating`, and a failed post must not reopen
              // the sheet or raise an error over a live urgent low.
              setTreatingSheetOpen(false);
              void episode.setTreatingIdentity(contactId);
            }}
            onDismissIdentity={() => setTreatingSheetOpen(false)}
          />
        )}
      </div>
    );
  }

  // Cycle 33: stale OR null-reading puts the hero into a "No Data"
  // state — replaces cycle-20's "Awaiting fresh reading" subline +
  // dimmed hero with explicit "No Data / Please check the CGM." copy.
  // The zone pill, member-head, KioskHeader chrome, and side-column
  // chart all stay rendered.
  // Cycle 468: a LOCKED member renders the stopped line whatever the last
  // confirmed sample says (the server withholds every newer reading, so the
  // last pre-lock number could otherwise linger for the confirmation bound —
  // security round 2). The follower's line replaces "No Data" for it.
  const noData = current == null || serviceLocked;
  const stoppedLine = serviceLocked
    ? followerStoppedLine(activeMember?.display_name ?? "")
    : null;

  // Cycle 375.1: while the feed is dark, kiosk/web collapse to a single
  // full-width column so the No-Data hero and the sequence strip own the
  // whole stage — the approved mockup's layout. The strip needs that width
  // to keep its headline (and the resume time inside it) on one line; in
  // the 472px hero column the time was the first thing to ellipsize away.
  // The chart is withheld deliberately: a dark feed has nothing live to
  // plot, and a last-known trace beside a "No Data" hero reads as coverage
  // that isn't there.
  const rootClass = `${gridClass} ${
    noData && (variant === "kiosk" || variant === "web")
      ? styles.noDataFull
      : ""
  }`;

  // Cycle 43 modals — shared by the portrait/kiosk/web layout and the
  // cycle-114 mobile-landscape split pane (same ActiveBolusesModal /
  // delete-confirm / ReadingDetailPopover flow on both).
  const modals = (
    <>
      <ActiveBolusesModal
        boluses={activeBolusTreatments}
        treatments={treatments}
        now={now}
        units={units}
        h24={h24}
        open={iobModalOpen}
        variant={variant}
        onClose={() => setIobModalOpen(false)}
        onRequestDelete={(t) => setPendingDelete(t)}
      />
      <ConfirmDeleteModal
        treatment={pendingDelete}
        h24={h24}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          const t = pendingDelete;
          setPendingDelete(null);
          if (t) void treatmentsApi.remove(t.id);
        }}
      />
      {readingDetail && (
        <ReadingDetailPopover
          reading={readingDetail.reading}
          treatments={treatments}
          thresholds={thresholds}
          units={units}
          h24={h24}
          anchorRect={readingDetail.anchorRect}
          onClose={() => setReadingDetail(null)}
        />
      )}
    </>
  );

  // ─── Cycle 145: alarm-clock mode (bedside idle face) ──────────────
  // Renders in place of the normal Now content, BELOW the alarm banner
  // row — Low/High banners + the AlarmSilenceGuard floor mount above
  // the face exactly as they do above the normal layout, and the
  // urgent takeover early-returned above this gate, so the urgent
  // family keeps its loud full-screen surface. The face owns no alarm
  // logic; entering/exiting never unmounts the episode bindings (the
  // episode hook lives in App).
  if (clockMode?.active) {
    // Cycle 167: a RESTING (snoozed / stopped) Low/High strip renders
    // fixed to the face's bottom edge while clock mode is active — the
    // face reserves that band so the heroes never sit under it, and
    // reclaims it when no strip is mounted. A freshly-sounding Low/High
    // is the cycle-166 full-face takeover (no reservation needed — the
    // face is covered); urgent is untouched.
    // ⚠ CYCLE 398: THIS CONDITION MIRRORS <LowAlarmBanner> / <HighAlarmBanner>'s
    // OWN MOUNT GATE, exactly as `clockUrgentStrip` mirrors <UrgentBanner>'s.
    // Both banners early-return null on `bannerSuppressedInRange`, so without
    // the same term here the face would reserve a band for a strip that is not
    // there — dead space under the heroes, which is the mirror-drift failure
    // the cycle-393/394 note warns about, in its other direction.
    // ⚠ CYCLE 454 (operator decision): a SOUNDING Low / High / Urgent High
    // no longer covers the face (the cycle-166 takeover is gone) — every
    // state of those banners is the fixed bottom strip, so the face reserves
    // for all of them. Still the banners' own mount gate, term for term:
    // `LowAlarmBanner` (low) and `HighAlarmBanner` (high + urgent high)
    // render whenever the episode is live and not stood down in range.
    const clockBottomStrip =
      episode.isLive &&
      (episode.episodeLevel === "low" ||
        episode.episodeLevel === "high" ||
        episode.episodeLevel === "urgent_high") &&
      !episode.bannerSuppressedInRange;
    // Cycle 394: the collapsed URGENT strip now pins to that same bottom
    // band on the kiosk (see the override in <UrgentBanner>), so the face
    // has to reserve for it too — otherwise the strip sits ON TOP of the
    // clock digits, which is the exact overlap `reserveBottomStrip`
    // exists to prevent. ⚠ This condition MIRRORS <UrgentBanner>'s own
    // mount gate; the two must be changed together or the face reserves a
    // band for a strip that isn't there (dead space under the heroes) or,
    // worse, fails to reserve for one that is.
    // ⚠ CYCLE 407: while DECLARED custody is open the treating screen
    // early-returns ABOVE this whole clock-mode block (exactly as the
    // urgent takeover always has), so the face is not on screen and the
    // `episode.treating` term below is reached only for a QUIET custody
    // (a snooze) — which still renders the collapsed strip on the face.
    // The mirror with <UrgentBanner> is intact; its main case just moved
    // up a level. Decided, not accidental: the check-in's Yes / No must be
    // answerable, and the bedside face has nowhere to put them.
    // Cycle 454: urgent low only — urgent high renders in `HighAlarmBanner`.
    const urgentStripMounted =
      episode.isLive &&
      episode.episodeLevel === "urgent_low" &&
      (episode.hasSnoozed ||
        episode.phase === "snoozed" ||
        episode.stopped ||
        episode.treating) &&
      !episode.bannerSuppressedInRange;
    const clockUrgentStrip = variant === "kiosk" && urgentStripMounted;
    // Cycle 452.2: off the kiosk the same strip stays in flow at the TOP,
    // over the full-bleed face — the face reserves a top band for it.
    const clockUrgentTopStrip = variant !== "kiosk" && urgentStripMounted;
    return (
      <>
        {/* Cycle 145.1: the face is absolute/fixed full-bleed on
            kiosk/web, so this in-flow banner row needs an explicit
            positive stacking position to paint ABOVE it (UrgentOverlay
            100 / AlarmSilenceGuard 60 already sit far above). */}
        <div
          className={`${styles.alarmRow} ${flavorClass}`}
          style={{ position: "relative", zIndex: 50 }}
        >
          <LowAlarmBanner episode={episode} units={units} variant={variant} />
          <HighAlarmBanner episode={episode} units={units} variant={variant} />
          <UrgentBanner episode={episode} units={units} variant={variant} />
          <AlarmSilenceGuard episode={episode} />
        </div>
        <AlarmClockMode
          socket={socket}
          variant={variant}
          units={units}
          dim={clockMode.dim}
          boluses={boluses}
          basal={basalForCard}
          // Cycle 402: the no-data strip occupies the same band as the
          // Low/High/Urgent ones. They are mutually exclusive by
          // construction — `noDataAlarmActive` requires `!episode.isLive`
          // in `useAlarmSounds` — so this OR can only ever reserve ONE
          // band, never stack two.
          reserveBottomStrip={
            clockBottomStrip || clockUrgentStrip || noDataAlarmActive
          }
          reserveTopStrip={clockUrgentTopStrip}
          memberName={activeMember?.display_name ?? null}
          onExit={() => {
            const back = clockMode.state.priorPath;
            clockMode.exit({ manual: true });
            if (back && back !== "/now") nav(back);
          }}
          onToggleDim={clockMode.toggleDim}
        />
      </>
    );
  }

  // ─── Cycle 142: shared bare-state pieces (mobile only) ────────────
  // One predicate drives every bare-state layout: is there active
  // rapid-acting (bolus/correction) IOB? `hasIOB` already reads
  // `activeBolusesForRings` (basal never counts — the ring-exclusion
  // invariant), so "rings would be non-empty" is exactly this. Cycle 227:
  // the IOB card's presence is driven by the shared `showIobCard` predicate
  // (IOB ∨ meal ∨ basal), so the cycle-142 `hasBasalContext` helper is no
  // longer needed.
  const signedDeltaStr =
    deltaMgdl !== null
      ? `${deltaMgdl >= 0 ? "+" : ""}${
          units === "mmol/L" ? toMmol(deltaMgdl).toFixed(1) : deltaMgdl
        }`
      : null;
  const statsTirColor =
    portraitStats.tir == null
      ? "var(--ink)"
      : portraitStats.tir >= 70
        ? "var(--zone-ok)"
        : portraitStats.tir >= 50
          ? "var(--zone-warn)"
          : "var(--zone-urgent)";
  // The three figures, shared verbatim by the chart-card strip, the
  // bare-state strip button, and the landscape chart strip (the trend
  // overlay renders its own copy from the same `portraitStats`).
  const statsCells = (
    <>
      <div className={styles.mpStat}>
        <span className={styles.mpStatNum} style={{ color: statsTirColor }}>
          {portraitStats.tir == null ? "—" : `${portraitStats.tir}%`}
        </span>
        <span className={styles.mpStatLabel}>in range</span>
      </div>
      <div className={styles.mpStat}>
        <span className={styles.mpStatNum}>
          {portraitStats.avg == null
            ? "—"
            : displayValue(portraitStats.avg, units)}
        </span>
        <span className={styles.mpStatLabel}>avg</span>
      </div>
      <div className={styles.mpStat}>
        <span className={styles.mpStatNum}>{portraitStats.lows}</span>
        <span className={styles.mpStatLabel}>
          {portraitStats.lows === 1 ? "low" : "lows"}
        </span>
      </div>
    </>
  );
  // The restructured three-row hero for the bare states: zone eyebrow on
  // top, number + mid-height-centered arrow, signed-delta sub-line below.
  // All from the existing zoneColor/zoneLabel/trendInfo/fmtAge plumbing.
  const renderBareHero = (valueSize: string, arrowSize: string) => (
    <div className={styles.bareHero}>
      {/* Cycle 343: the eyebrow sits directly on MobileBackground (aurora
          or a user photo), so no fixed text color can guarantee contrast
          against the photo. Cycle 451.2: it takes the shared status-pill
          look — white ink on an opaque state-tinted backing — and the small
          dot keeps the zone hue. */}
      <div className={`${styles.bareEyebrow} ${statusPillClass(zoneTone(zone))}`}>
        <span
          className={styles.mpStatusDot}
          style={{ background: zoneColor(zone) }}
        />
        {zoneLabel(zone)}
      </div>
      <div className={styles.bareValueRow}>
        <HeroValue
          value={current ? displayValue(current.sgv, units) : "—"}
          zone={zone}
          stale={stale}
          pulse={isUrgent}
          size={valueSize}
        />
        <TrendArrow
          glyph={trend.glyph}
          zone={zone}
          stale={stale}
          size={arrowSize}
        />
      </div>
      <div className={styles.bareSub}>
        <span className="tnum">
          {trend.label}
          {signedDeltaStr !== null ? ` ${signedDeltaStr} ${units}` : ""}
          {" · "}
          {fmtAge(ageMin)}
        </span>
      </div>
    </div>
  );
  // The trend overlay (cycle 156): owns its own window state, seeded
  // to 24h on every open — it never drives `chartHours`, so the inline
  // chart's window survives the modal. The screen's loaded series ride
  // along as the reuse source; `screenHours` tells the sheet what they
  // cover, and it fetches (same wrappers, same limits) only for a
  // window the screen hasn't loaded.
  const trendSheet = (
    <TrendSheet
      open={trendSheetOpen}
      onClose={() => setTrendSheetOpen(false)}
      readings={chartReadings}
      treatments={treatments}
      alerts={chartAlerts}
      screenHours={chartHours}
      thresholds={thresholds}
      units={units}
      h24={h24}
      nowMs={now}
      windowChoices={windowChoices}
      windowLabel={(h) => windowLabel(h as ChartHours)}
      live={socket.status === "open"}
      onReadingTap={chartOnReadingTap}
    />
  );

  // ─── Cycle 114: mobile landscape split-pane ───────────────────────
  // Rotating a phone to landscape squeezes the portrait column stack
  // into ~390 px of height and collapses the measured-fill chart. This
  // dedicated layout — nav rail (in MobileFrame) → stats column → chart
  // pane — is gated strictly on `variant === "mobile" && isLandscape`;
  // portrait, kiosk, and web take the unchanged return below. The alarm
  // banners (viewport-relative, in-flow above the pane) and the cycle-43
  // modals are shared. The expand chevron is not rendered here (the chart
  // is always fill mode); `chartExpanded` is left untouched so rotating
  // back to portrait restores it. `chartHours` is component state and
  // survives rotation (no refetch — the fetch effect keys off
  // `chartHours`/`latest.id`/member, none of which rotation changes).
  if (mobileLandscape) {
    return (
      <>
        <div className={`${styles.alarmRow} ${flavorClass}`}>
          <LowAlarmBanner episode={episode} units={units} variant={variant} />
          <HighAlarmBanner episode={episode} units={units} variant={variant} />
          <UrgentBanner episode={episode} units={units} variant={variant} />
          <AlarmSilenceGuard episode={episode} />
        </div>
        <div className={`${styles.root} ${styles.mobileLandscape}`}>
          {/* Stats column. With bolus IOB OR a basal on board (cycle 147 —
              the basal now has its own meaningful outer ring, so a
              basal-only state shows the rings pager instead of the
              retired cycle-142 chip): the cycle-115 glance + rings pager.
              With nothing on board: the pager is hidden, the
              restructured hero centers vertically, and a Quick Log
              button rounds out the column. */}
          {hasIOB || basal != null ? (
            <div className={styles.lsStatsCol}>
              <div className={styles.lsGlance}>
                <div className={styles.lsMemberRel}>
                  {activeMember?.display_name ?? "—"}
                </div>
                {noData ? (
                  stoppedLine ? (
                    <div className={styles.lsStopped} data-testid="member-stopped-line">
                      {stoppedLine}
                    </div>
                  ) : (
                  <div
                    className={styles.lsNoData}
                    style={{ color: "var(--zone-urgent)" }}
                  >
                    No Data
                  </div>
                  )
                ) : (
                  <>
                    <div className={styles.lsValueRow}>
                      <HeroValue
                        value={current ? displayValue(current.sgv, units) : "—"}
                        zone={zone}
                        stale={stale}
                        pulse={isUrgent}
                        size="clamp(40px, 12vw, 48px)"
                      />
                      <TrendArrow
                        glyph={trend.glyph}
                        zone={zone}
                        stale={stale}
                        size={30}
                      />
                    </div>
                    <div className={styles.lsMeta}>
                      <Pill tone={tone} size="md" appearance="status">
                        {zoneLabel(zone)}
                      </Pill>
                      <span className="tnum">
                        {fmtAge(ageMin)} · {units}
                      </span>
                    </div>
                  </>
                )}
              </div>

              <IobPager
                boluses={boluses}
                treatments={treatments}
                now={now}
                h24={h24}
                basal={basalForCard}
                onDismissDue={dismissBasalDue}
                onDismissCard={dismissIobCard}
                onRingsTap={
                  activeBolusTreatments.length > 0
                    ? () => setIobModalOpen(true)
                    : undefined
                }
              />
            </div>
          ) : (
            <div className={styles.lsStatsCol}>
              <div className={styles.lsMemberRel}>
                {activeMember?.display_name ?? "—"}
              </div>
              <div className={styles.lsBareMiddle}>
                {noData ? (
                  stoppedLine ? (
                    <div className={styles.lsStopped} data-testid="member-stopped-line">
                      {stoppedLine}
                    </div>
                  ) : (
                  <div
                    className={styles.lsNoData}
                    style={{ color: "var(--zone-urgent)" }}
                  >
                    No Data
                  </div>
                  )
                ) : (
                  renderBareHero("clamp(44px, 13vw, 56px)", "26px")
                )}
                {/* Cycle 147: the cycle-142 basal chip is gone — a basal
                    now shows the rings pager branch above (it has its own
                    outer ring), so this branch only renders when nothing
                    is on board. */}
              </div>
              <button
                type="button"
                className={styles.lsQuickLog}
                onClick={() => {
                  hapticSelect();
                  goToLog("/log");
                }}
              >
                + Quick Log
              </button>
              {logBlocked && (
                <p className={styles.logBlockedLine} role="status">
                  {LOG_BLOCKED_LINE}
                </p>
              )}
            </div>
          )}

          {/* Chart pane: window pills + live badge (no expand chevron) +
              measured-fill chart filling the remaining width/height. */}
          <div className={styles.lsChartPane}>
            <div className={styles.sparkHead}>
              <div className={styles.windowRow}>
                {windowChoices.map((h) => (
                  <button
                    key={h}
                    type="button"
                    className={`${styles.windowBtn} ${
                      chartHours === h ? styles.windowBtnActive : ""
                    }`}
                    onClick={() => {
                      hapticSelect();
                      setChartHours(h);
                    }}
                  >
                    {windowLabel(h)}
                  </button>
                ))}
              </div>
              <span className={`${styles.sparkBadge} tnum`}>live</span>
            </div>
            <div ref={chartContainerRef} className={styles.chartContainer}>
              <HistoryChart
                readings={chartReadings}
                treatments={treatments}
                alerts={chartAlerts}
                thresholds={thresholds}
                rangeStartMs={chartRangeStart}
                rangeEndMs={chartRangeEnd}
                height={chartHeight}
                units={units}
                h24={h24}
                panSliceMs={panSliceMs}
                pinchZoom
                flickMomentum={chartFlick}
                nowMs={now}
                showNowLine={chartShowNowLine}
                onReadingTap={chartOnReadingTap}
                showTooltips
                partitionLabel={windowLabel(chartHours)}
              />
            </div>
            {/* Cycle 142: TIR / avg / lows under the landscape graph —
                both IOB states, same shared computation as portrait. */}
            <div className={styles.mpStatsStrip}>{statsCells}</div>
          </div>
        </div>
        {modals}
      </>
    );
  }

  // ─── Cycle 116: mobile portrait redesign ──────────────────────────
  // A dedicated, de-duplicated portrait column: clean hero (value +
  // arrow), one status line, a compact horizontal IOB card, and a
  // full-bleed chart card with a stats strip and swipe-to-change-window.
  // The identity row lives in the MobileHeader (large-title mode, hidden
  // for single-member). Kiosk, web, and the cycle-114/115 landscape
  // layout take the unchanged returns above/below.
  if (mobilePortrait) {
    // Cycle 122/208: the combined IOB card (rings + meal context + basal).
    // Cycle 147: the basal rides the third ring + the Long-acting line.
    // Tapping the rings opens ActiveBolusesModal. Cycle 208: the card↔chart
    // swipe pager + its inline chart page are gone; the graph is reached via
    // the stats strip → TrendSheet.
    // Cycle 227: the card is a bottom-anchored GLASS panel (cardClassName +
    // --card-glass-* tokens), fills+shrinks within the flex column
    // (fillHeight), and wraps the MealContextLines detail in a bounded,
    // scrollbar-free, edge-faded scroll region (detailScroll) so tall
    // content scrolls INSIDE the card — the rings/center stay fixed above.
    // (BasalLine was retired in cycle 147; the basal is the third ring +
    // the Long-acting line inside MealContextLines, so it scrolls with the
    // detail rather than as a separate fixed footer.)
    const iobCard = (
      <div className={styles.mpIobCard}>
        <BolusRings
          boluses={boluses}
          now={now}
          basal={basalForCard}
          stack
          cardClassName={styles.mpIobGlass}
          fillHeight
          detailScroll
          detailScrollClassName={styles.iobDetailScroll}
          onDismissDue={dismissBasalDue}
          onDismissCard={dismissIobCard}
          onTap={
            activeBolusTreatments.length > 0
              ? () => setIobModalOpen(true)
              : undefined
          }
        >
          <MealContextLines
            blocks={blocks}
            h24={h24}
            variant="mobile"
            basal={basalForCard}
            onLogBasal={onLogBasal}
          />
        </BolusRings>
        {logBlocked && (
          <p className={styles.logBlockedLine} role="status">
            {LOG_BLOCKED_LINE}
          </p>
        )}
      </div>
    );

    // Cycle 156: the tappable In range / Avg / Lows strip opens the
    // TrendSheet graph overlay. Shared by BOTH portrait sub-states (bare and
    // IOB-present) so the graph is reached the same way everywhere — there is
    // no longer any inline portrait chart.
    const statsStripTrend = (
      <button
        type="button"
        className={`${styles.mpStatsStrip} ${styles.bareStatsStrip}`}
        onClick={() => {
          hapticSelect();
          setTrendSheetOpen(true);
        }}
        aria-label="Open the glucose trend chart"
      >
        {statsCells}
      </button>
    );

    // ── Cycle 227: unified mobile-portrait Now ──
    // Both sub-states (bare = no rapid-acting IOB, and IOB-present) share
    // ONE layout: a centered hero block sitting HIGH directly beneath the
    // identity header (status state / number+arrow / subline — the rounded
    // hero face is scoped to the number+arrow via `.mpRoot .bareValueRow`),
    // the empty vertical space falling BELOW it, and the bottom-anchored
    // glass card group (IOB card + tappable In range / Avg / Lows strip).
    // The graph is reached via the stats strip → TrendSheet (no inline
    // chart, pager, or dots — removed in cycle 208). The IOB card is
    // omitted when there's no IOB, meal, or basal (`showIobCard`), and the
    // stats strip stays bottom-anchored with no overlap.
    return (
      <>
        <div className={`${styles.alarmRow} ${flavorClass}`}>
          <LowAlarmBanner episode={episode} units={units} variant={variant} />
          <HighAlarmBanner episode={episode} units={units} variant={variant} />
          <UrgentBanner episode={episode} units={units} variant={variant} />
          <AlarmSilenceGuard episode={episode} />
        </div>
        {/* Cycle 315: Android-mobile-only urgent-alarm-setup nudge. Normal
            flow BELOW the alarm-banner row + withheld entirely while an
            episode is live, so it can never obscure or displace an alarm
            control or the glucose display. Self-gates off Android-native
            (renders null everywhere else); dismissible per session. */}
        {!episode.isLive ? <AlarmReliabilityBanner /> : null}
        <div className={styles.mpRoot}>
          <div className={styles.mpHeroBlock}>
            {noData ? (
              stoppedLine ? (
                <div className={styles.mpStopped} data-testid="member-stopped-line">
                  {stoppedLine}
                </div>
              ) : (
              <div
                className={styles.mpNoData}
                style={{ color: "var(--zone-urgent)" }}
              >
                <div className={styles.mpNoDataHead}>No Data</div>
                <div className={styles.mpNoDataSub}>Please check the CGM.</div>
              </div>
              )
            ) : (
              renderBareHero(
                "clamp(88px, 26vw, 132px)",
                "clamp(44px, 13vw, 62px)",
              )
            )}
            {/* Cycle 222: orb relocated to the frame-level SpeakingBanner. */}
          </div>
          {/* Cycle 354: "Is the member away or changing the sensor?" — answering
              pauses the blackout pages (server-clamped, auto-expiring);
              never covers an alarm surface.
              Cycle 375.2: its OWN flex slot (a direct child of .mpRoot),
              not a tail on the fixed-height hero block. The slot absorbs
              the free space and scrolls under pressure, so a tall prompt
              can never be overlapped by the bottom-anchored card group —
              the failure mode that made these buttons untappable. */}
          {noData ? (
            <div className={styles.mpSensorPause}>
              <SensorPausePrompt
                memberId={activeId}
                memberName={activeMember?.display_name ?? ""}
                dark={noData}
                mode="strip"
                h24={h24}
                now={now}
                variant={variant}
              />
            </div>
          ) : null}
          {/* Bottom-anchored glass card group: the combined IOB card (when
              there's something on board) above the tappable stats strip,
              pinned to the bottom above the tab bar. The empty gap above
              collapses first; only then does the IOB card's inner detail
              region shrink — hero number + tab bar stay fully visible. */}
          <div className={styles.mpReveal}>
            {showIobCard ? iobCard : null}
            {statsStripTrend}
          </div>
        </div>
        {trendSheet}
        {modals}
      </>
    );
  }

  return (
    <>
      {/* Cycle 91.2: the Low/High/Urgent alarm strips + the silence-guard
          floor sit in a row-grid that mirrors the Now-screen content
          columns, so on kiosk + web the strip occupies ONLY the right
          (IOB/chart) column — right-anchored, its left edge flush with the
          IOB card, with a rounded bottom-left corner (see `.alarmRow` in
          the CSS module). The row reserves height only when a banner is
          actually mounted (the components return null otherwise), so it
          pushes hero + IOB down together exactly as the full-width strip
          did. On mobile (single column) the row is full-width, unchanged. */}
      <div
        className={`${styles.alarmRow} ${flavorClass} ${
          // Cycle 394: kiosk only — move the strip to the bottom of the
          // screen, full width, IN FLOW (see `.alarmRowBottom`). The
          // modifier is added HERE and never in the clock-mode return, so
          // the bedside face keeps its own fixed-to-the-bottom treatment.
          variant === "kiosk" ? styles.alarmRowBottom : ""
        }`}
      >
        <LowAlarmBanner episode={episode} units={units} variant={variant} />
        <HighAlarmBanner episode={episode} units={units} variant={variant} />
        <UrgentBanner episode={episode} units={units} variant={variant} />
        {/* Cycle 63 floor / cycle 81 phase-live gate: bulletproof Stop
          control. Self-gates to render only when the episode is
          phase-live (`isLive`) AND none of the rich surfaces above
          already own a control for this episode (it stays mutually
          exclusive — see AlarmSilenceGuard). The control gate is now
          `isLive`, decoupled from `alarmActive` (the live reading's
          zone), so a live episode in the recovery-dwell window can
          never sit on screen with the loop able to sound and no
          control: loop could sound ⇒ phase active ⇒ isLive ⇒ a
          control is on screen. */}
        <AlarmSilenceGuard episode={episode} />
      </div>
      <div className={rootClass}>
        {/* Hero column. Always in the tree; cycle 48 hides it via the
            `.heroColCollapsed` (display:none) modifier when the chart
            is expanded on mobile/web so the chart can claim the whole
            content area. Kiosk never expands, so it stays visible
            there exactly as in cycle 47. */}
        <div
          className={`${styles.heroCol} ${
            // Cycle 57.6: mobile + IOB keeps the hero visible even
            // when the chart is expanded — the user wants the headline
            // value + delta to stay readable above the chart.
            chartExpanded && !mobileIobActive ? styles.heroColCollapsed : ""
          }`}
        >
          <div className={styles.memberHead}>
            <div className={styles.memberName}>
              {activeMember?.display_name ?? "—"}
            </div>
            <div className={styles.memberRel}>
              {relationshipLabel(activeMember)}
            </div>
          </div>
          <div className={styles.heroMeta}>
            <Pill
              tone={noData ? "urgent" : tone}
              size={variant === "kiosk" || variant === "web" ? "lg" : "md"}
              appearance="status"
            >
              {/* Cycle 451.2: the dot keeps the STATE color — the label is
                  white now, so `currentColor` would turn the dot white too. */}
              <span
                style={{
                  width: variant === "kiosk" ? 9 : 6,
                  height: variant === "kiosk" ? 9 : 6,
                  borderRadius: 999,
                  background: noData ? "var(--zone-urgent)" : zoneColor(zone),
                  animation:
                    isUrgent && !noData
                      ? "pulseDot 1.4s ease-in-out infinite"
                      : "none",
                }}
              />
              {noData ? "No Data" : zoneLabel(zone)}
            </Pill>
            <span
              style={{
                // Cycle 297: web age ("just now") a touch larger + brighter.
                fontSize:
                  variant === "kiosk" ? 18 : variant === "web" ? 15 : 13,
                fontWeight: variant === "web" ? 600 : 400,
                color: variant === "web" ? "var(--ink-2)" : "var(--ink-3)",
              }}
            >
              {current ? fmtAge(ageMin) : "—"}
            </span>
          </div>

          {noData ? (
            <>
              <div
                className={styles.noData}
                style={{ color: "var(--zone-urgent)" }}
              >
                <div className={styles.noDataHead}>No Data</div>
                <div className={styles.noDataSub}>Please check the CGM.</div>
              </div>
              {/* Cycle 354: the sensor-pause prompt (see mobile mount). */}
              <SensorPausePrompt
                memberId={activeId}
                memberName={activeMember?.display_name ?? ""}
                dark={noData}
                mode="strip"
                h24={h24}
                now={now}
                variant={variant}
              />
            </>
          ) : (
            <>
              <div
                className={`${styles.heroRow} ${
                  variant === "mobile" ? "" : styles.heroRowCentered
                }`}
              >
                <HeroValue
                  value={current ? displayValue(current.sgv, units) : "—"}
                  zone={zone}
                  stale={stale}
                  pulse={isUrgent}
                  size={heroFontSize}
                />
                <div
                  className={styles.trendCol}
                  style={{
                    paddingTop: variant === "mobile" ? 8 : 0,
                  }}
                >
                  <TrendArrow
                    glyph={trend.glyph}
                    zone={zone}
                    stale={stale}
                    size={trendSize}
                    weight={trendWeight}
                  />
                  {variant === "mobile" && (
                    <div className={styles.trendUnits}>{units}</div>
                  )}
                </div>
              </div>

              <div className={styles.subline}>
                <span
                  style={{
                    // Cycle 297: web trend word ("Steady") bigger + bolder.
                    fontSize:
                      variant === "kiosk" ? 22 : variant === "web" ? 20 : 14,
                    fontWeight: variant === "web" ? 700 : 500,
                  }}
                >
                  {trend.label}
                </span>
                {deltaMgdl !== null && (
                  <>
                    <span
                      style={{
                        width: variant === "kiosk" ? 4 : 3,
                        height: variant === "kiosk" ? 4 : 3,
                        borderRadius: 999,
                        background: "var(--ink-4)",
                      }}
                    />
                    <span
                      className="tnum"
                      style={{ fontSize: variant === "kiosk" ? 22 : 14 }}
                    >
                      {deltaMgdl >= 0 ? "+" : ""}
                      {units === "mmol/L"
                        ? toMmol(deltaMgdl).toFixed(1)
                        : deltaMgdl}{" "}
                      {units}
                    </span>
                  </>
                )}
              </div>
            </>
          )}
          {/* Cycle 222: the cycle-214 Pulse-Core orb beneath the hero number
              is gone — it (and its caption) now live in the frame-level
              SpeakingBanner, shown only while speaking. The hero column ends
              at the number. */}
        </div>

        {/* Side column.
            When IOB is active: BolusRings + 3h sparkline (taller than
            cycle 32 — the cycle-33 QuickLogRow removal reclaimed
            vertical space).
            When IOB is empty: a single multi-hour HistoryChart panel
            — same panel treatment as the sparkline so it reads as the
            same surface family. The cycle-33 height bumps absorb the
            QuickLogRow's vertical real estate without overflowing the
            kiosk 1024×600 frame. */}
        <div className={styles.sideCol}>
          {/* Cycle 74b: the IOB card mounts on every variant whether or
              not IOB is active — an empty (dimmed 0.0 / I.O.B) state +
              "No insulin on board" line when no bolus is in flight, so a
              caregiver can always tell at a glance whether recent
              insulin is covering a high. Still hidden when the chart is
              expanded (the hero column is hidden too in that mode).
              Cycle 48: the wrapper lets the `.mobile .bolusWrap { flex:
              0 0 auto }` rule pin its height. onTap (→ ActiveBolusesModal)
              is wired only when there are active boluses. */}
          {/* Cycle 101: voice-logged doses that couldn't reach the server
              are held in a persistent queue and synced on reconnect. Show
              an unobtrusive hint so a queued dose isn't invisible. Kiosk
              only, single compact line above the IOB card — never overlaps
              the hero/card/footer (it's in normal flow inside .sideCol). */}
          {variant === "kiosk" && pendingSyncCount > 0 && !chartExpanded && (
            <div
              role="status"
              aria-live="polite"
              style={{
                display: "inline-flex",
                alignSelf: "center",
                alignItems: "center",
                gap: 6,
                margin: "0 0 6px",
                padding: "3px 10px",
                borderRadius: 999,
                fontSize: "var(--type-floor)",
                fontWeight: 600,
                color: "var(--ink-2)",
                background: "var(--bg-elev)",
                border: "0.5px solid var(--line-strong)",
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: "var(--zone-warn)",
                }}
              />
              {pendingSyncCount} pending sync
            </div>
          )}
          {/* Cycle 121: the IOB card is hidden entirely when there's no
              bolus IOB, no meal context, and no basal (showIobCard false) —
              the window pills + chart card then occupy the column with no
              empty gap. Cycle 147: a basal renders as the third outermost
              ring + the violet Long-acting line (a basal-only card shows
              just the basal ring; the due nudge lives on the line). */}
          {!chartExpanded && showIobCard && (
            <div className={styles.bolusWrap}>
              <BolusRings
                boluses={boluses}
                now={now}
                basal={basalForCard}
                onDismissDue={dismissBasalDue}
                onDismissCard={dismissIobCard}
                onTap={
                  activeBolusTreatments.length > 0
                    ? () => setIobModalOpen(true)
                    : undefined
                }
              >
                <MealContextLines
                  blocks={blocks}
                  h24={h24}
                  variant={variant}
                  basal={basalForCard}
                  onLogBasal={onLogBasal}
                />
              </BolusRings>
              {logBlocked && (
                <p className={styles.logBlockedLine} role="status">
                  {LOG_BLOCKED_LINE}
                </p>
              )}
            </div>
          )}
          {hasIOB ? (
            <>
              {mobileIobActive && !chartExpanded ? (
                /* Cycle 57.6: on mobile + IOB-active the chart panel
                   collapses to a single tappable card — a big gold
                   pulsating triangle, the words "View Graph", and a
                   green "Live" badge. Tap → chartExpanded flips true
                   and the full HistoryChart opens BELOW the hero
                   (the .mobile.iobActive.chartExpanded CSS rule keeps
                   the hero visible). Cycle 57.9: a sibling "Add
                   Insulin" CTA below the card deep-links to the Log
                   screen's Insulin tab. */
                <>
                  <button
                    type="button"
                    // Cycle 169: frosted glass (mobile-IOB-only card).
                    className={`${styles.viewGraphCard} ${styles.glass}`}
                    onClick={() => setChartExpanded(true)}
                    aria-label="View glucose chart"
                  >
                    {/* 0.64.2: replaced the cycle-57.6 hardcoded
                        gold CSS-border triangle with a themed SVG —
                        a circular accent-soft badge with an
                        upward-trending mini sparkline so the affordance
                        reads as "tap to see chart" rather than the
                        old "play" cue. Theme-tracking via var(--accent)
                        + var(--accent-soft). */}
                    <svg
                      className={styles.viewGraphIcon}
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <circle cx="12" cy="12" r="11" />
                      <path
                        d="M5 15 L9 11 L13 13 L19 7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className={styles.viewGraphLabel}>View Graph</span>
                    <span className={styles.viewGraphLive}>Live</span>
                  </button>
                  <button
                    type="button"
                    className={styles.addInsulinCard}
                    onClick={() => goToLog("/log")}
                    aria-label="Quick log"
                  >
                    <span className={styles.addInsulinPlus} aria-hidden>
                      +
                    </span>
                    <span className={styles.addInsulinLabel}>
                      Quick Log
                    </span>
                  </button>
                  {logBlocked && (
                    <p className={styles.logBlockedLine} role="status">
                      {LOG_BLOCKED_LINE}
                    </p>
                  )}
                </>
              ) : variant === "kiosk" ||
              variant === "mobile" ||
              chartExpanded ? (
                /* Cycle 34 (kiosk) + cycle 46 (mobile): the IOB-active
                   side column fills the space below the rings with the
                   same HistoryChart panel the IOB-empty branch uses
                   (window pills + live badge), so the chart claims
                   whatever vertical space remains down to the
                   FooterTabs nav instead of leaving the bottom empty.
                   Cycle 46 retired the mobile 3h <Sparkline> here: it
                   was bound to the WS `recentReadings` buffer (blank on
                   cold mount); the chart is REST-seeded via
                   `chartReadings` so it populates instantly. Web keeps
                   the cycle-33 3h sparkline below. */
                <div
                  className={`${styles.sparkPanel} ${styles.sparkPanelFill}`}
                >
                  <div className={styles.sparkHead}>
                    <div className={styles.windowRow}>
                      {windowChoices.map((h) => (
                        <button
                          key={h}
                          type="button"
                          className={`${styles.windowBtn} ${
                            chartHours === h ? styles.windowBtnActive : ""
                          }`}
                          onClick={() => setChartHours(h)}
                        >
                          {windowLabel(h)}
                        </button>
                      ))}
                    </div>
                    {headRight}
                  </div>
                  <div
                    ref={chartContainerRef}
                    className={styles.chartContainer}
                  >
                    <HistoryChart
                      readings={chartReadings}
                      treatments={treatments}
                      alerts={chartAlerts}
                      thresholds={thresholds}
                      rangeStartMs={chartRangeStart}
                      rangeEndMs={chartRangeEnd}
                      height={chartHeight}
                      units={units}
                      h24={h24}
                      panSliceMs={panSliceMs}
                      pinchZoom
                      flickMomentum={chartFlick}
                      nowMs={now}
                      showNowLine={chartShowNowLine}
                      onReadingTap={chartOnReadingTap}
                      showTooltips
                      partitionLabel={windowLabel(chartHours)}
                    />
                  </div>
                </div>
              ) : (
                <div className={styles.sparkPanel}>
                  <div className={styles.sparkHead}>
                    <span className={styles.sparkLabel}>Last 3 hours</span>
                    {headRight}
                  </div>
                  <Sparkline
                    readings={socket.recentReadings}
                    thresholds={thresholds}
                    currentZone={zone}
                    height={120}
                  />
                </div>
              )}
            </>
          ) : (
            <div
              className={`${styles.sparkPanel} ${
                variant === "kiosk" || variant === "mobile" || chartExpanded
                  ? styles.sparkPanelFill
                  : ""
              }`}
            >
              <div className={styles.sparkHead}>
                <div className={styles.windowRow}>
                  {windowChoices.map((h) => (
                    <button
                      key={h}
                      type="button"
                      className={`${styles.windowBtn} ${
                        chartHours === h ? styles.windowBtnActive : ""
                      }`}
                      onClick={() => setChartHours(h)}
                    >
                      {windowLabel(h)}
                    </button>
                  ))}
                </div>
                <span className={`${styles.sparkBadge} tnum`}>live</span>
              </div>
              {/* Cycle 34 (kiosk) + cycle 46 (mobile) measure the chart
                  container so HistoryChart fills the panel
                  top-to-bottom. Web keeps the cycle-33 fixed 440 px
                  height since its layout isn't height-constrained the
                  same way. */}
              <div
                ref={chartContainerRef}
                className={styles.chartContainer}
              >
                <HistoryChart
                  readings={chartReadings}
                  treatments={treatments}
                  alerts={chartAlerts}
                  thresholds={thresholds}
                  rangeStartMs={chartRangeStart}
                  rangeEndMs={chartRangeEnd}
                  height={chartHeight}
                  units={units}
                  h24={h24}
                  panSliceMs={panSliceMs}
                  pinchZoom
                  flickMomentum={chartFlick}
                  nowMs={now}
                  showNowLine={chartShowNowLine}
                  onReadingTap={chartOnReadingTap}
                  showTooltips
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Cycle 43: the inline IOB editor + its sibling delete-confirm +
          cycle-48 reading-detail popover — extracted to `modals` above
          (cycle 114) and shared with the landscape split pane. The
          z-order is unchanged (ConfirmDeleteModal 365 over
          ActiveBolusesModal 356; ReadingDetailPopover 354/355). */}
      {modals}
    </>
  );
}
